import { z } from 'zod';
import {
  AXIS_LABEL_BUDGET_CH,
  AXIS_LABEL_MAX_LINES,
} from '@/components/charts/axis-label-budget';
import { CONTENT_LABEL_BUDGET_CH } from '@/components/charts/content-label-budget';
import { LUCIDE_ICON_NAMES } from './lucide-icons.generated';

/**
 * Character caps for the label fields the LLM fills, derived from the CSS budgets the
 * modules actually render with (`components/charts/axis-label-budget.ts` for anything
 * near an axis, `components/charts/content-label-budget.ts` for node, item and cell
 * text). A label that wraps may fill `AXIS_LABEL_MAX_LINES` lines of its `ch` budget;
 * one drawn as SVG or `whitespace-nowrap` text cannot wrap at all and gets a single line.
 */
const LABEL_MAX_CHARS = {
  /** X-axis tick on every cartesian chart. 14ch over 3 lines. */
  axisTick: AXIS_LABEL_BUDGET_CH.xAxisTick * AXIS_LABEL_MAX_LINES,
  /** X-axis tick composed as `<label> <index>`, so the index needs room too. */
  axisTickPrefix: AXIS_LABEL_BUDGET_CH.xAxisTick * AXIS_LABEL_MAX_LINES - 4,
  /** Legend entry. `LegendLabel` budgets 20ch and wraps under the same line ceiling. */
  legend: CONTENT_LABEL_BUDGET_CH.label * AXIS_LABEL_MAX_LINES,
  /** Axis or category label that cannot wrap. One line of the category budget. */
  axisNoWrap: AXIS_LABEL_BUDGET_CH.yAxisCategory,
  /** Text under or beside a glyph, logo or marker. 12ch over 3 lines. */
  caption: CONTENT_LABEL_BUDGET_CH.caption * AXIS_LABEL_MAX_LINES,
  /** Node name, tier label, step name — text in or on an element. 20ch over 3 lines. */
  label: CONTENT_LABEL_BUDGET_CH.label * AXIS_LABEL_MAX_LINES,
  /** Title or sub-line of a grid cell, several side by side. 24ch over 3 lines. */
  cell: CONTENT_LABEL_BUDGET_CH.cell * AXIS_LABEL_MAX_LINES,
  /** Title of a row that spans the module width. 32ch over 3 lines. */
  row: CONTENT_LABEL_BUDGET_CH.row * AXIS_LABEL_MAX_LINES,
} as const;

const SINGLE_LINE_MAX_CHARS = {
  tile: CONTENT_LABEL_BUDGET_CH.cell,
  label: CONTENT_LABEL_BUDGET_CH.label,
  cell: CONTENT_LABEL_BUDGET_CH.cell,
  row: CONTENT_LABEL_BUDGET_CH.row,
} as const;

const GrayscaleField = z.boolean().optional().describe(
  "Render the module in monochrome greyscale instead of the project's colorful accent palette. Default false."
);

export const MODULE_ACCENT_SLOT_COUNT = 5;
export const MODULE_SPACING_VALUES = ['compact', 'default', 'relaxed'] as const;
export const MODULE_ALIGN_VALUES = ['left', 'center'] as const;

const AccentField = z.number().int().min(0).max(MODULE_ACCENT_SLOT_COUNT - 1).optional().describe(
  "Index of the handyout palette color this module uses as its accent: 0 to 4, one per " +
  "`--handyout-chart-1..5` slot (0 = slot 1). Default 0. Leave unset unless the author asks for a " +
  "specific accent."
);

const SpacingField = z.enum(MODULE_SPACING_VALUES).optional().describe(
  "Vertical breathing room inside the module: 'compact', 'default' or 'relaxed'. Default 'default'. " +
  "Leave unset unless the author asks for it."
);

const AlignField = z.enum(MODULE_ALIGN_VALUES).optional().describe(
  "Horizontal alignment of the module's title and content block: 'left' or 'center'. Default 'left'. " +
  "Leave unset unless the author asks for it."
);

const ShowTitleField = z.boolean().optional().describe(
  "Show the module's title above its content. Default true. Set false only when the author asks to " +
  "hide the title; the title text itself stays required."
);

export const ModuleStyleFields = {
  accent: AccentField,
  spacing: SpacingField,
  align: AlignField,
  showTitle: ShowTitleField,
};

export const ModuleStyleSchema = z.object(ModuleStyleFields);
export type ModuleStyle = z.infer<typeof ModuleStyleSchema>;

export const MODULE_STYLE_DEFAULTS = {
  accent: 0,
  spacing: 'default',
  align: 'left',
  showTitle: true,
} as const satisfies Required<ModuleStyle>;

const ElementIdField = z.string().optional().describe(
  "Stable id of this element, unique within its list. Older content may lack it. When updating a " +
  "module, keep every existing id unchanged and give each new element a fresh short id."
);

export const ModuleChartDataPointSchema = z.object({
  name: z.string().max(LABEL_MAX_CHARS.axisTick).describe(
    "The x-axis value of this point — the category it occupies on the band scale (or the slice " +
    "name for pie). Any short string: 'Q1', 'Week 1', 'Enterprise', 'Slide 4', 'PDF attachment'. " +
    "It is not a date field and bar/line/area have no time scale, so nothing here gets reformatted " +
    "or spaced by date — an ISO string would be drawn verbatim. Write the text that should stand " +
    "under the tick, phrase every point of one chart the same way, and order the points the way " +
    "they should read left to right."
  ).meta({ translatable: true }),
}).catchall(z.union([z.string(), z.number()])).describe(
  "A single data point for bar/line/area/pie/radial-bar charts. `name` is the categorical " +
  "x-axis label (or slice label for pie). Add any numeric properties (e.g. 'value', 'views', " +
  "'interactions') for the y-values — one per series. Example: {name: 'Jan', views: 120, " +
  "interactions: 40} renders two series ('views' and 'interactions') at x='Jan'."
);

export const ModuleChartSeriesMetaSchema = z.object({
  key: z.string().describe(
    "The property name on each data-point object that carries this series' numeric values. " +
    "Must match a key on your data. E.g. if points are {name:'Jan', views:120}, key='views'."
  ),
  label: z.string().min(1).max(LABEL_MAX_CHARS.legend).describe(
    "Required. Short legend name of this series, shown in the legend and the tooltip ('Views', " +
    "'Revenue'). A few words at most, written for the reader: the raw `key` is a technical name " +
    "and never appears on the chart."
  ).meta({ translatable: true }),
  color: z.string().optional().describe(
    "CSS color for this series. Use a handyout palette slot, 'var(--chart-1)' through 'var(--chart-5)'. " +
    "Falls back to the palette slot at this series' index."
  ),
}).describe(
  "Metadata for a single chart series: the data key it reads and the legend name the reader sees. " +
  "Write one entry per numeric key on your data points."
);

const ChartSeriesField = z.array(ModuleChartSeriesMetaSchema).min(1).describe(
  "Required, at least one entry: one entry per numeric key of the data points, in the order the " +
  "series should be drawn. `key` names the property on the data points, `label` is the short legend " +
  "name the reader sees. Example: points {name: 'Jan', views: 120, signups: 40} need " +
  "[{key: 'views', label: 'Views'}, {key: 'signups', label: 'Sign-ups'}]."
);

const XAxisLabelField = z.string().max(LABEL_MAX_CHARS.axisNoWrap).describe(
  "Name of the x-axis. Required, not decorative: an axis nobody named is an axis the reader " +
  "cannot interpret, so every chart carries one. A noun or a short noun phrase ('Trading day', " +
  "'Booking period', 'Format'), never a sentence, and never a unit in brackets: where the module " +
  "offers an axis `unit` field the unit belongs there. The name is drawn in the row beneath the " +
  "plot, and on a 375px screen every character it takes is a character the plot loses — the " +
  "shorter, the more chart is left. On a time axis the name replaces the part all tick labels " +
  "share instead of repeating it: write the shared period ('March 2026') or the dimension itself " +
  "('Booking period'), never the single dates the ticks already show."
).meta({ translatable: true });

const YAxisLabelField = z.string().max(LABEL_MAX_CHARS.axisNoWrap).describe(
  "Name of the y-axis. Required, not decorative: an axis nobody named is an axis the reader " +
  "cannot interpret, so every chart carries one. A noun or a short noun phrase ('Net engagement', " +
  "'Share price', 'Time on slide'), never a sentence, and never a unit in brackets: where the " +
  "module offers an axis `unit` field the unit belongs there. The name is drawn in the narrow " +
  "rail left of the plot, and on a 375px screen every character that rail takes is a character " +
  "the plot loses — the shorter, the more chart is left. Name what is measured, not how it is " +
  "scaled; on a time axis write the shared period ('March 2026') or the dimension itself " +
  "('Booking period') instead of repeating what the ticks already show."
).meta({ translatable: true });

export const ModuleCategorySchema = z.object({
  id: z.string().describe(
    "Stable id referenced by items via `categoryId`. Use short kebab-case (e.g. 'done', 'in-review', 'blocked')."
  ),
  label: z.string().max(LABEL_MAX_CHARS.legend).describe(
    "Human-readable label shown in legend, tooltips, and section headers. A few words at most."
  ).meta({ translatable: true }),
  color: z.string().describe(
    "CSS color for the category — used for legend marker, item stroke/fill, and group accents. " +
    "Use one of the five handyout palette slots, 'var(--chart-1)' through 'var(--chart-5)'; they follow the author's palette, the module accent and grayscale. Literal colors are legacy."
  ),
}).describe(
  "Reusable category descriptor. Modules with grouping/status semantics accept a `categories` array " +
  "of these; items reference them via a `categoryId` field. Enables free-form categorization with " +
  "custom labels and palette-aligned colors."
);

export const VisualRefIconSchema = z.object({
  type: z.literal('icon'),
  name: z.string().describe(
    "Lucide-icon slug (kebab-case, e.g. 'target', 'trending-up'). See lucide-icons.generated.ts."
  ),
});

export const VisualRefImageSchema = z.object({
  type: z.literal('image'),
  src: z.string().describe(
    "Public URL or /path/*.png|jpg|svg. Rendered as an <img> constrained to the module's visual slot."
  ),
});

export const VisualRefLogoSchema = z.object({
  type: z.literal('logo'),
  slug: z.string().describe(
    "Company-logo shorthand 'lndev:<slug>' — resolves to https://cdn.handyout.com/lndev-logos/logos/<slug>.svg. " +
    "Slug follows ln-dev7/logos-apps naming (lowercase, hyphens; add '-wordmark' for icon+text variants)."
  ),
});

export const VisualRefNumberSchema = z.object({
  type: z.literal('number'),
  value: z.number().describe("Number rendered in tabular-nums style inside the visual slot."),
});

export const VisualRefSchema = z.discriminatedUnion('type', [
  VisualRefIconSchema,
  VisualRefImageSchema,
  VisualRefLogoSchema,
  VisualRefNumberSchema,
]).describe(
  "Discriminated visual reference for module-slots that accept multiple representations of the same " +
  "concept. Modules may narrow this union to a subset (e.g. only 'icon' and 'logo' for activity-feed). " +
  "Rendered uniformly by <VisualRefRender>."
);

export const ModuleReferenceAreaSchema = z.object({
  y1: z.number().describe("Bottom y-value of the band (inclusive)."),
  y2: z.number().describe("Top y-value of the band (inclusive)."),
  label: z.string().max(LABEL_MAX_CHARS.caption).optional().describe(
    "Short pill label rendered inside the band, anchored top-right (e.g. 'Target range'). " +
    "One or two words - the pill sits on top of the plot area and eats into the chart when it grows."
  ).meta({ translatable: true }),
  color: z.string().optional().describe(
    "Stroke and fill base color for the band. Use a handyout palette slot, 'var(--chart-1)' through 'var(--chart-5)'. Defaults to var(--chart-1)."
  ),
  variant: z.enum(['solid', 'pattern']).optional().describe(
    "Fill style: 'solid' (default) is a flat translucent color; 'pattern' overlays a repeating SVG " +
    "pattern for a stripier look."
  ),
  pattern: z.enum(['diagonal', 'horizontal', 'vertical', 'cross', 'dots']).optional().describe(
    "Which pattern preset to use when variant='pattern'. Ignored for 'solid'."
  ),
}).describe(
  "A shaded horizontal band highlighting a y-value range on the chart (e.g. a target zone, an " +
  "acceptable operating range). Renders under the data series with faded left/right edges."
);

export const CHARTCOLORS = [
  'var(--primary)',
  '#0EA5E9',
  '#10B981',
  '#8B5CF6',
  '#F59E0B',
  '#EC4899',
  '#14B8A6',
  '#6366F1',
  '#84CC16',
  '#F97316',
];

export const CHARTCOLORS_GRAYSCALE = [
  '#171717',
  '#404040',
  '#737373',
  '#a3a3a3',
  '#c4c4c4',
  '#d4d4d4',
  '#525252',
  '#8a8a8a',
  '#b4b4b4',
  '#dedede',
];

export function chartColors(grayscale?: boolean): string[] {
  return grayscale ? CHARTCOLORS_GRAYSCALE : CHARTCOLORS;
}

export type ChartThreshold = {
  value: number;
  label?: string;
  color?: string;
  axis?: 'x' | 'y';
};

export const IconNames = LUCIDE_ICON_NAMES;

export const IconNameSchema = z.enum(IconNames);

export type IconName = z.infer<typeof IconNameSchema>;

export const ProcessStatusSchema = z.enum(['done', 'active', 'pending']).describe(
  "Lifecycle stage of a step/node in a process diagram: 'done' (completed), " +
  "'active' (in progress, pulsing), 'pending' (not started, dashed outline)."
);
export type ProcessStatus = z.infer<typeof ProcessStatusSchema>;

export const ModuleImageDataSchema = z.object({
  src: z.string(),
  alt: z.string().optional().meta({ translatable: true }),
  description: z.string().optional().meta({ translatable: true })
});

export const ProfileSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  avatar: z.string().optional(),
  bio: z.string().optional().meta({ translatable: true }),
  role: z.string().optional().meta({ translatable: true }),
  website: z.string().url().optional(),
  twitter: z.string().optional(),
  linkedin: z.string().optional(),
  instagram: z.string().optional(),
  credits: z.number().optional(),
});

export const ModuleProgressCompositionSegmentsSchema = z.record(z.string(), z.number()).describe(
  "Count per category, keyed by category id. Without `data.categories` the legacy keys " +
  "'done' | 'review' | 'blocked' | 'todo' apply with their default labels and colors. " +
  "With `data.categories` set, use each category's `id` as key for free N-way composition. " +
  "The first category is the one summarized as 'X / total' next to the row label."
);

export const ModuleProgressCompositionItemSchema = z.object({
  id: z.string(),
  label: z.string().max(LABEL_MAX_CHARS.row).describe(
    "Row label (e.g. 'Frontend refactor'). It shares one line with the count on the right, so keep it " +
    "to a few words."
  ).meta({ translatable: true }),
  segments: ModuleProgressCompositionSegmentsSchema,
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
});

export const ModuleProgressListStatusSchema = z.enum([
  'on-track',
  'at-risk',
  'blocked',
]).describe("Health signal shown as color hint + trailing label.");

export const ModuleProgressListItemSchema = z.object({
  id: z.string(),
  label: z.string().max(LABEL_MAX_CHARS.row).describe(
    "Row label (e.g. 'Auth v2 rewrite'). It shares one line with the percentage on the right, so keep " +
    "it to a few words."
  ).meta({ translatable: true }),
  value: z.number().min(0).max(100).describe("Progress 0..100."),
  subtext: z.string().max(LABEL_MAX_CHARS.row).describe(
    "Sub-line under the bar (e.g. '3 days left'). It shares one line with the status label on the " +
    "right and every extra line pushes the next row down."
  ).meta({ translatable: true }),
  status: ModuleProgressListStatusSchema.optional().describe(
    "Deprecated: legacy status enum ('on-track'/'at-risk'/'blocked'). Prefer `categoryId` with " +
    "`data.categories` for free-form health signals + palette colors."
  ),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
});

export const ModuleQuoteDataSchema = z.object({
  quote: z.string().meta({ translatable: true }),
  author: z.string().optional(),
  role: z.string().optional().meta({ translatable: true })
});

export const ModuleChartBarSchema = z.object({
  id: z.string(),
  type: z.literal('bar'),
  title: z.string().meta({ translatable: true }),
  data: z.array(ModuleChartDataPointSchema).optional(),
  xAxisLabel: XAxisLabelField,
  yAxisLabel: YAxisLabelField,
  series: ChartSeriesField,
  showBrush: z.boolean().optional().describe(
    "Adds a mini brush strip below the chart for pan/zoom. Recommended when data has 30+ points."
  ),
  showGrid: z.boolean().optional().describe(
    "Show horizontal grid lines. Default true. Turn off for a decorative / hero-style chart."
  ),
  showMarkers: z.boolean().optional().describe(
    "Show hover dots on each bar top. Default false. Turn on when the exact numbers matter."
  ),
  showLegend: z.boolean().optional().describe(
    "Show the legend row. Default auto — visible when 2+ series are present."
  ),
  fadeEdges: z.boolean().optional().describe(
    "Fade the outermost bars at chart edges. Default true. Turn off inside brush strips."
  ),
  variant: z.enum(['bars', 'squares']).optional().describe(
    "'bars' (default) = classic filled bar columns. 'squares' = stacked squares with a pattern-line " +
    "track underneath, GitHub-contribution style. Use 'squares' for a modern stat visualization."
  ),
  view3d: z.boolean().optional().describe(
    "Isometric 3D view. Default false: the flat bar chart. When true the chart opens as isometric 3D " +
    "bars from a fixed angle (categories along the front, each series in its own row behind) with a " +
    "2D/3D toggle that morphs back into a flat bar chart. Only for a striking visual of a category " +
    "comparison the presenter wants to show off; never when the audience must read or compare exact " +
    "values, and never with negative values. In the 3D view the brush is disabled and the reference " +
    "area fades out. Charts with more than 1200 bars (categories x series) always render flat without " +
    "the toggle."
  ),
  referenceArea: ModuleReferenceAreaSchema.optional().describe(
    "Optional threshold band highlighted on the y-axis (target zone, acceptable range)."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Compare discrete values across categories (products, teams, regions). Best for 3-10 items. " +
  "Every numeric key on a data point is one series, and several series render as grouped bars " +
  "side by side within each category — never stacked, which this module cannot do. Two or three " +
  "series per category stay readable on a phone; more than that makes each bar too thin to compare."
);

export const ModuleChartLineSchema = z.object({
  id: z.string(),
  type: z.literal('line'),
  title: z.string().meta({ translatable: true }),
  data: z.array(ModuleChartDataPointSchema).optional(),
  xAxisLabel: XAxisLabelField,
  yAxisLabel: YAxisLabelField,
  series: ChartSeriesField,
  showBrush: z.boolean().optional().describe(
    "Adds a mini brush strip below the chart for pan/zoom. Recommended when data has 30+ points."
  ),
  showGrid: z.boolean().optional().describe(
    "Show horizontal grid lines. Default true. Turn off for a decorative / hero-style chart."
  ),
  showMarkers: z.boolean().optional().describe(
    "Show hover dots at each data point. Default false. Turn on when the exact numbers matter."
  ),
  showLegend: z.boolean().optional().describe(
    "Show the legend row. Default auto — visible when 2+ series are present."
  ),
  fadeEdges: z.boolean().optional().describe(
    "Fade the line stroke at chart edges. Default true. Turn off inside brush strips."
  ),
  curve: z.enum(['natural', 'monotone-x', 'linear', 'step', 'basis', 'catmull-rom']).optional().describe(
    "D3 curve interpolation for the line: 'natural' (smooth cardinal spline — default), " +
    "'monotone-x' (smooth, monotonic in X — no overshoot at extremes), 'linear' (straight " +
    "segments between points), 'step' (staircase, holds y between points), 'basis' (smoothest " +
    "but least accurate — passes near but not through points), 'catmull-rom' (natural spline " +
    "passing through every point)."
  ),
  referenceArea: ModuleReferenceAreaSchema.optional().describe(
    "Optional threshold band highlighted on the y-axis (target zone, acceptable range)."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show trend over an ordered dimension (time, sequence). Best for 5+ data points.");

export const ModuleChartPieSchema = z.object({
  id: z.string(),
  type: z.literal('pie'),
  title: z.string().meta({ translatable: true }),
  data: z.array(ModuleChartDataPointSchema).optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show proportional breakdown of a whole. Best for 2-5 slices; avoid when slices are similar in size.");

export const ModuleImageSchema = z.object({
  id: z.string(),
  type: z.literal('image'),
  data: ModuleImageDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Display a single image from the slide's pictures array.");

export const ModuleProgressBarDataSchema = z.union([
  z.object({
    variant: z.literal('composition'),
    items: z.array(ModuleProgressCompositionItemSchema).describe(
      "Rows with composition bars. Each row's `segments` holds one count per category id."
    ),
    categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
      "Optional free-form categories. When set, the composition bar renders one slice per category " +
      "in this order, using each category's `color`, and `segments` is keyed by these ids. When " +
      "omitted, the legacy 4-way preset done / review / blocked / todo applies."
    ),
  }),
  z.object({
    variant: z.literal('progress'),
    items: z.array(ModuleProgressListItemSchema).describe(
      "Rows with a single-value progress bar, sub-text, and a health status label."
    ),
    categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
      "Optional free-form categories. When set, each item's `categoryId` picks color + label; the " +
      "legacy `status` enum is ignored."
    ),
  }),
]);

export const ModuleProgressBarSchema = z.object({
  id: z.string(),
  type: z.literal('progress'),
  title: z.string().meta({ translatable: true }),
  data: ModuleProgressBarDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a set of progress rows in a 3-row × N-col overview grid. Two variants: " +
  "'composition' renders multi-segment bars (free categories, or the legacy done/review/blocked/todo preset); " +
  "'progress' renders single-value bars with a health label (free categories, or the legacy " +
  "on-track / at-risk / blocked preset)."
);

export const ModuleQuoteSchema = z.object({
  id: z.string(),
  type: z.literal('quote'),
  title: z.string().meta({ translatable: true }),
  data: ModuleQuoteDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Highlight a testimonial or quotation with author attribution.");

export const ModuleIntroSchema = z.object({
  id: z.string(),
  type: z.literal('intro'),
  title: z.string().meta({ translatable: true }),
  summary: z.string().meta({ translatable: true }),
  author: ProfileSchema,
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Auto-generated cover slide — never select manually, rendered statically for slide 1.");

export const ModuleTOCItemSchema: z.ZodType<{
  title: string;
  slide_range?: [number, number];
  children?: Record<string, { title: string; slide_range?: [number, number]; children?: any }>;
}> = z.object({
  title: z.string().meta({ translatable: true }),
  slide_range: z.tuple([z.number(), z.number()]).optional().describe(
    "Inclusive [startSlide, endSlide] range this TOC item covers (1-indexed slide numbers)."
  ),
  children: z.lazy(() => z.record(z.string(), ModuleTOCItemSchema)).optional().describe(
    "Nested TOC entries, keyed by section id."
  ),
});

export const ModuleTOCSchema = z.object({
  id: z.string(),
  type: z.literal('toc'),
  title: z.string().meta({ translatable: true }),
  data: z.record(z.string(), ModuleTOCItemSchema),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Auto-generated table of contents — never select manually, rendered statically.");

export const ModuleFeedbackSchema = z.object({
  id: z.string(),
  type: z.literal('feedback'),
  title: z.string().meta({ translatable: true }),
  data: z.object({
    session_id: z.string()}),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Auto-generated audience feedback prompt — never select manually, rendered statically on final slide.");

export const ModuleChartScatterPointSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number().optional().describe(
    "Optional third dimension mapped to point size (bubble radius). Omit for uniform-size points."
  ),
});

export const ModuleChartScatterSeriesSchema = z.object({
  id: ElementIdField,
  name: z.string().max(LABEL_MAX_CHARS.legend).meta({ translatable: true }),
  data: z.array(ModuleChartScatterPointSchema),
});

export const ModuleChartScatterDataSchema = z.object({
  series: z.array(ModuleChartScatterSeriesSchema),
});

export const ModuleChartScatterSchema = z.object({
  id: z.string(),
  type: z.literal('scatter'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartScatterDataSchema.optional(),
  xAxisLabel: XAxisLabelField,
  xAxisUnit: z.string().optional().describe(
    "Short unit appended after the x-axis name ('s', 'kg', '%'). Keep it out of `xAxisLabel`."
  ),
  yAxisLabel: YAxisLabelField,
  yAxisUnit: z.string().optional().describe(
    "Short unit appended after the y-axis name ('s', 'kg', '%'). Keep it out of `yAxisLabel`."
  ),
  zAxisLabel: z.string().max(LABEL_MAX_CHARS.axisTick).optional().describe(
    "Name of the bubble-size dimension. A noun phrase, not a sentence. Only meaningful when the " +
    "points carry a `z` value."
  ).meta({ translatable: true }),
  zAxisUnit: z.string().optional().describe(
    "Short unit for the bubble-size dimension. Keep it out of `zAxisLabel`."
  ),
  zAxisRange: z.tuple([z.number(), z.number()]).optional().describe(
    "Optional [minRadius, maxRadius] in pixels for the bubble-size scale. Defaults to a sensible auto range."
  ),
  showBrush: z.boolean().optional().describe(
    "Adds a mini brush strip below the chart for pan/zoom. Recommended for 30+ points."
  ),
  showGrid: z.boolean().optional().describe(
    "Show grid lines. Default true."
  ),
  showMarkers: z.boolean().optional().describe(
    "Emphasize point markers on hover. Default false."
  ),
  showLegend: z.boolean().optional().describe(
    "Show the legend row. Default auto — visible when 2+ series."
  ),
  fadeEdges: z.boolean().optional().describe(
    "Fade points near chart edges. Default true."
  ),
  referenceArea: ModuleReferenceAreaSchema.optional().describe(
    "Optional threshold band highlighted on the y-axis."
  ),
  view3d: z.boolean().optional().describe(
    "Isometric 3D view. Default false: the flat scatter plot. When true the points open as dots " +
    "in an isometric 3D box seen from a fixed angle (x along the width, y as height, every series in " +
    "its own lane behind the first) with drop lines and shadows on the floor, and a 2D/3D toggle that " +
    "morphs back into the flat scatter plot. `z` is not plotted in either view. Only for a striking " +
    "visual of how series separate; never when the audience must read exact positions. The brush stays " +
    "active in both views; the reference area fades out in the 3D view. Charts with more than 600 " +
    "points always render flat without the toggle."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show correlation between two numeric variables across many data points.");

export const ModuleChartAreaSchema = z.object({
  id: z.string(),
  type: z.literal('area'),
  title: z.string().meta({ translatable: true }),
  data: z.array(ModuleChartDataPointSchema).optional(),
  xAxisLabel: XAxisLabelField,
  yAxisLabel: YAxisLabelField,
  series: ChartSeriesField,
  showBrush: z.boolean().optional().describe(
    "Adds a mini brush strip below the chart for pan/zoom. Recommended when data has 30+ points."
  ),
  showGrid: z.boolean().optional().describe(
    "Show horizontal grid lines. Default true."
  ),
  showMarkers: z.boolean().optional().describe(
    "Show hover dots at each data point. Default false."
  ),
  showLegend: z.boolean().optional().describe(
    "Show the legend row. Default auto — visible when 2+ series are present."
  ),
  fadeEdges: z.boolean().optional().describe(
    "Fade the fill at chart edges. Default true."
  ),
  curve: z.enum(['natural', 'monotone-x', 'linear', 'step', 'basis', 'catmull-rom']).optional().describe(
    "D3 curve interpolation for the area outline: 'natural' (smooth cardinal spline — default), " +
    "'monotone-x' (smooth, monotonic in X — no overshoot), 'linear' (straight segments), " +
    "'step' (staircase), 'basis' (smoothest but passes near not through points), 'catmull-rom' " +
    "(natural spline through every point)."
  ),
  referenceArea: ModuleReferenceAreaSchema.optional().describe(
    "Optional threshold band highlighted on the y-axis."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show cumulative or filled trend over time. Emphasizes volume more than line does.");

export const PyramidVisualRefSchema = z.discriminatedUnion('type', [
  VisualRefNumberSchema,
  VisualRefIconSchema,
  VisualRefLogoSchema,
]).describe(
  "Optional per-tier glyph rendered inside the node. Defaults to auto-numbering (tier index + 1). " +
  "Use `{type:'icon', name:'target'}` for a lucide slug or `{type:'logo', slug:'lndev:stripe'}` for a brand logo."
);

export const ModulePyramidTierSchema = z.object({
  id: z.string().describe("Stable per-tier id — used for hover/selection sync."),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Short tier name (e.g. 'Managed', 'Defined'). It is drawn inside the tier and repeated in the " +
    "legend, so one or two words."
  ).meta({ translatable: true }),
  value: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Free-form tier value shown in the legend (e.g. '28%', '12k', 'L3'). Rendered verbatim next to " +
    "the tier label, which is what limits its length."
  ),
  status: ProcessStatusSchema.optional().describe(
    "Deprecated: legacy status enum. Prefer omitting — palette colors rotate through chart-1..chart-N by index."
  ),
  color: z.string().optional().describe(
    "Optional per-tier color, a handyout palette slot 'var(--chart-1)' through 'var(--chart-5)'. Overrides the palette default for both the pyramid tier itself and its legend marker."
  ),
  variant: z.enum(['filled', 'dashed']).optional().describe(
    "Per-tier fill style. 'filled' (default) = solid tier. 'dashed' = outlined tier with a dashed border and no fill — use for prospective, optional or not-yet-reached tiers."
  ),
  visual: PyramidVisualRefSchema.optional(),
});

export const ModulePyramidDataSchema = z.object({
  tiers: z.array(ModulePyramidTierSchema).describe(
    "3-6 stacked tiers. Tier index 0 is drawn at the top of the pyramid regardless of `inverted`."
  ),
});

export const ModulePyramidSchema = z.object({
  id: z.string(),
  type: z.literal('pyramid'),
  title: z.string().meta({ translatable: true }),
  data: ModulePyramidDataSchema.optional(),
  variant: z.enum(['nodes', 'segmented']).optional().describe(
    "'nodes' (default) = dashed pyramid outline with a numbered node at each tier's center. " +
    "'segmented' = filled tier trapezoids. Per-tier fill style is controlled by each tier's own `variant` field."
  ),
  inverted: z.boolean().optional().describe(
    "false (default) = narrow apex at top, wide base at bottom (classic pyramid). " +
    "true = wide base at top, narrow apex at bottom (funnel look)."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-6 status-tracked tiers as a pyramid. Each tier carries a value label and a status " +
  "(done/active/pending). Best for maturity models, capability tiers, or any tiered progression."
);

export const DOTTED_MAP_COUNTRY_CODES = [
  'AFG', 'AGO', 'ALB', 'ARE', 'ARG', 'ARM', 'ATA', 'ATF', 'AUS', 'AUT',
  'AZE', 'BDI', 'BEL', 'BEN', 'BFA', 'BGD', 'BGR', 'BHS', 'BIH', 'BLR',
  'BLZ', 'BMU', 'BOL', 'BRA', 'BRN', 'BTN', 'BWA', 'CAF', 'CAN', 'CHE',
  'CHL', 'CHN', 'CIV', 'CMR', 'COD', 'COG', 'COL', 'CRI', 'CUB', 'CYP',
  'CZE', 'DEU', 'DJI', 'DNK', 'DOM', 'DZA', 'ECU', 'EGY', 'ERI', 'ESH',
  'ESP', 'EST', 'ETH', 'FIN', 'FJI', 'FLK', 'FRA', 'GAB', 'GBR', 'GEO',
  'GHA', 'GIN', 'GMB', 'GNB', 'GNQ', 'GRC', 'GRL', 'GTM', 'GUF', 'GUY',
  'HND', 'HRV', 'HTI', 'HUN', 'IDN', 'IND', 'IRL', 'IRN', 'IRQ', 'ISL',
  'ISR', 'ITA', 'JAM', 'JOR', 'JPN', 'KAZ', 'KEN', 'KGZ', 'KHM', 'KOR',
  'KWT', 'LAO', 'LBN', 'LBR', 'LBY', 'LKA', 'LSO', 'LTU', 'LUX', 'LVA',
  'MAR', 'MDA', 'MDG', 'MEX', 'MKD', 'MLI', 'MLT', 'MMR', 'MNE', 'MNG',
  'MOZ', 'MRT', 'MWI', 'MYS', 'NAM', 'NCL', 'NER', 'NGA', 'NIC', 'NLD',
  'NOR', 'NPL', 'NZL', 'OMN', 'PAK', 'PAN', 'PER', 'PHL', 'PNG', 'POL',
  'PRI', 'PRK', 'PRT', 'PRY', 'PSE', 'QAT', 'ROU', 'RUS', 'RWA', 'SAU',
  'SDN', 'SEN', 'SLB', 'SLE', 'SLV', 'SOM', 'SRB', 'SSD', 'SUR', 'SVK',
  'SVN', 'SWE', 'SWZ', 'SYR', 'TCD', 'TGO', 'THA', 'TJK', 'TKM', 'TLS',
  'TTO', 'TUN', 'TUR', 'TWN', 'TZA', 'UGA', 'UKR', 'URY', 'USA', 'UZB',
  'VEN', 'VNM', 'VUT', 'YEM', 'ZAF', 'ZMB', 'ZWE',
] as const;

export const DottedMapCountryCodeSchema = z.enum(DOTTED_MAP_COUNTRY_CODES);
export type DottedMapCountryCode = z.infer<typeof DottedMapCountryCodeSchema>;

export const ModuleDottedMapPinSchema = z.object({
  id: ElementIdField,
  lat: z.number(),
  lng: z.number(),
  label: z.string().max(LABEL_MAX_CHARS.label).optional().describe(
    "Place name shown next to the pin (e.g. 'Stockholm'). It is drawn on the map itself, where a long " +
    "label collides with neighbouring pins."
  ).meta({ translatable: true }),
  color: z.string().optional(),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`. When set + category " +
    "present, the pin's color comes from the category (unless the explicit `color` field is also set)."
  ),
});

export const ModuleDottedMapDataSchema = z.object({
  pins: z.array(ModuleDottedMapPinSchema),
  countries: z.array(DottedMapCountryCodeSchema).optional().describe(
    "ISO alpha-3 country codes to include in the rendered map. Omit to render the full world."
  ),
  height: z.number().optional().describe("Map height in pixels."),
  grid: z.enum(['diagonal', 'vertical']).optional().describe(
    "Dot layout pattern: 'diagonal' offsets alternating rows for a honeycomb feel; 'vertical' is a strict column grid."
  ),
  backgroundColor: z.string().optional().describe("Map background color (hex, rgb, or CSS var)."),
  dotColor: z.string().optional().describe("Color for the background country dots."),
  pinRadius: z.number().optional().describe("Radius of pins in pixels."),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories. When set, legend renders category chips with hover-filter " +
    "(dims non-matching pins). Colors come from categories."
  ),
});

export const ModuleDottedMapSchema = z.object({
  id: z.string(),
  type: z.literal('dotted-map'),
  title: z.string().meta({ translatable: true }),
  data: ModuleDottedMapDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show geographic pins on a country-scoped dotted world map. Countries array uses ISO alpha-3 codes.");

export const ModuleChartRadialBarSchema = z.object({
  id: z.string(),
  type: z.literal('radial-bar'),
  title: z.string().meta({ translatable: true }),
  data: z.array(ModuleChartDataPointSchema).optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show 3-6 metrics as concentric radial bars (progress around a circle).");

export const ModuleChartCandlestickPointSchema = z.object({
  date: z.string().describe(
    "Session date as an ISO date string, 'YYYY-MM-DD'. The x-axis tick labels are derived from " +
    "it — '2026-03-02' renders as 'Mar 2' — so never write display text here, and keep the " +
    "points in chronological order. Use trading days only: weekend gaps read as a real pause, " +
    "an unbroken run of seven days a week does not."
  ),
  open: z.number().describe(
    "Opening price. Set it to the previous point's `close`, otherwise the series reads as a " +
    "chain of unrelated days."
  ),
  high: z.number().describe(
    "Session high. Must be at or above both `open` and `close`, or the wick renders inside the body."
  ),
  low: z.number().describe(
    "Session low. Must be at or below both `open` and `close`, or the wick renders inside the body."
  ),
  close: z.number().describe(
    "Closing price. Above `open` draws a rising candle, below it a falling one — vary the " +
    "direction so both colours appear."
  ),
});

export const ModuleChartCandlestickDataSchema = z.object({
  points: z.array(ModuleChartCandlestickPointSchema),
  positiveColor: z.string().optional().describe(
    "Color for up-days where close >= open (defaults to emerald)."
  ),
  negativeColor: z.string().optional().describe(
    "Color for down-days where close < open (defaults to red)."
  ),
});

export const ModuleChartCandlestickSchema = z.object({
  id: z.string(),
  type: z.literal('candlestick'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartCandlestickDataSchema.optional(),
  xAxisLabel: XAxisLabelField,
  yAxisLabel: YAxisLabelField,
  showBrush: z.boolean().optional().describe(
    "Adds a mini brush strip below the chart for pan/zoom. Recommended when data has 30+ points."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show OHLC (open/high/low/close) financial series over time.");

export const ModuleChartFunnelStageSchema = z.object({
  id: ElementIdField,
  label: z.string().max(LABEL_MAX_CHARS.axisNoWrap).describe(
    "Stage name (e.g. 'Visitors', 'Qualified'). One or two words - it is drawn on a single line inside the segment and cannot wrap."
  ).meta({ translatable: true }),
  value: z.number(),
  displayValue: z.string().optional().describe(
    "Pre-formatted display text overriding the raw numeric value (e.g. '1.2k', '$85M')."
  ),
  color: z.string().optional().describe(
    "Optional per-stage color override, a handyout palette slot 'var(--chart-1)' through 'var(--chart-5)'. Falls back to palette gradient."
  ),
});

export const ModuleChartFunnelDataSchema = z.object({
  stages: z.array(ModuleChartFunnelStageSchema),
  orientation: z.enum(['horizontal', 'vertical']).optional().describe(
    "'horizontal' = funnel narrows left-to-right. 'vertical' = funnel narrows top-to-bottom."
  ),
  color: z.string().optional().describe(
    "Base funnel color. Individual stage `color` overrides this per-stage."
  ),
  layers: z.number().int().min(1).max(6).optional().describe(
    "Number of stacked color-tinted layers per stage (1-6). More = deeper visual gradient."
  ),
  edges: z.enum(['curved', 'straight']).optional().describe(
    "'curved' = smooth bezier transitions between stages. 'straight' = angled linear transitions."
  ),
});

export const ModuleChartFunnelSchema = z.object({
  id: z.string(),
  type: z.literal('funnel'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartFunnelDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show pipeline drop-off across 3-6 sequential stages (visitors → leads → customers).");

export const ModuleChartHeatmapPointSchema = z.object({
  x: z.union([z.string().meta({ translatable: true }), z.number()]).describe(
    "Column this cell belongs to. Strings are column labels ('Week 1', 'Q3'); numbers are day " +
    "offsets and switch the module into its calendar reading. Mixing both in one data set is " +
    "not supported. Every distinct value becomes one column, so keep the set small: 15 columns " +
    "is where a 375px phone starts scrolling sideways."
  ),
  y: z.union([z.string().meta({ translatable: true }), z.number()]).describe(
    "Row this cell belongs to, same rules as `x`. Numbers plus exactly 7 rows read as weekdays. " +
    "Rows never scroll, so 7 to 10 is the practical ceiling."
  ),
  value: z.number().describe(
    "Cell magnitude. Mapped onto the 7-step colour scale, so the spread across all points is " +
    "what the reader sees — a set where every value is close together renders almost flat."
  ),
});

export const ModuleChartHeatmapDataSchema = z.object({
  points: z.array(ModuleChartHeatmapPointSchema),
});

export const ModuleChartHeatmapSchema = z.object({
  id: z.string(),
  type: z.literal('heatmap'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartHeatmapDataSchema.optional(),
  view3d: z.boolean().optional().describe(
    "Isometric 3D view. Default false: the flat heat map. When true the grid opens as an isometric " +
    "3D skyline seen from a fixed angle where every cell is a box whose height follows its value, " +
    "with a 2D/3D toggle that morphs back into the flat heat map. Only for a striking visual of where the " +
    "peaks sit, e.g. a busy season in a calendar grid; never when the audience must read or compare " +
    "exact cell values, and never with negative values. Grids with more than 400 cells always " +
    "render flat without the toggle."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show a 2D grid of intensity values via color scale — activity, correlation, or density.");

export const ModuleChartProfitLossPointSchema = z.object({
  date: z.string().describe(
    "Calendar date of this value as an ISO date string, 'YYYY-MM-DD'. The x-axis tick labels are " +
    "derived from it — '2026-03-02' renders as 'Mar 2' — so never write display text here, and " +
    "keep the points in chronological order."
  ),
  value: z.number(),
});

export const ModuleChartProfitLossDataSchema = z.object({
  points: z.array(ModuleChartProfitLossPointSchema),
  positiveColor: z.string().optional().describe(
    "Color for segments where value >= 0 (defaults to emerald)."
  ),
  negativeColor: z.string().optional().describe(
    "Color for segments where value < 0 (defaults to red)."
  ),
});

export const ModuleChartProfitLossSchema = z.object({
  id: z.string(),
  type: z.literal('profit-loss'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartProfitLossDataSchema.optional(),
  xAxisLabel: XAxisLabelField,
  yAxisLabel: YAxisLabelField,
  showMarkers: z.boolean().optional().describe(
    "Show hover dots at each data point. Default false. Turn on when the exact numbers matter."
  ),
  curve: z.enum(['natural', 'monotone-x', 'linear', 'step', 'basis', 'catmull-rom']).optional().describe(
    "D3 curve interpolation between points: 'natural' (smooth cardinal spline — default), " +
    "'monotone-x' (smooth, no overshoot), 'linear' (straight segments — most literal for P/L), " +
    "'step' (staircase), 'basis' (smoothest), 'catmull-rom' (natural spline through every point)."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show signed values over time with segments colored green (positive) or red (negative).");

export const ModuleChartRadarMetricSchema = z.object({
  key: z.string().describe(
    "Unique metric id. Each series' `values` map uses this key to look up that series' score for this metric."
  ),
  label: z.string().max(LABEL_MAX_CHARS.axisNoWrap).describe(
    "Metric name shown at the radar axis tip. One or two words - the tip label sits on a single line and cannot wrap."
  ).meta({ translatable: true }),
});

export const ModuleChartRadarSeriesSchema = z.object({
  id: ElementIdField,
  label: z.string().max(LABEL_MAX_CHARS.legend).meta({ translatable: true }),
  color: z.string().optional(),
  values: z.record(z.string(), z.number()).describe(
    "Map from each metric key (see ModuleChartRadarMetricSchema.key) to this series' numeric score."
  ),
});

export const ModuleChartRadarDataSchema = z.object({
  metrics: z.array(ModuleChartRadarMetricSchema),
  series: z.array(ModuleChartRadarSeriesSchema),
});

export const ModuleChartRadarSchema = z.object({
  id: z.string(),
  type: z.literal('radar'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartRadarDataSchema.optional(),
  showMarkers: z.boolean().optional().describe(
    "Show hover dots on each polygon vertex. Default false. Turn on when the exact numbers matter."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Compare 3-8 dimensions across 1-4 categories on a polar radar.");

export const ModuleChartSankeyNodeSchema = z.object({
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Node name shown next to its column (e.g. 'Forwarded internally'). One or two words - it is drawn " +
    "beside the node bar and a long name overlaps the ribbons."
  ).meta({ translatable: true }),
  category: z.enum(['source', 'landing', 'outcome']).optional().describe(
    "Optional 3-column grouping — 'source' (left), 'landing' (middle), 'outcome' (right). " +
    "Used to color nodes and organize flows visually."
  ),
});

export const ModuleChartSankeyLinkSchema = z.object({
  source: z.number().int().describe("Zero-based index into the `nodes` array — the flow's start."),
  target: z.number().int().describe("Zero-based index into the `nodes` array — the flow's end."),
  value: z.number().describe("Flow magnitude — sets the ribbon thickness."),
});

export const ModuleChartSankeyDataSchema = z.object({
  nodes: z.array(ModuleChartSankeyNodeSchema),
  links: z.array(ModuleChartSankeyLinkSchema),
});

export const ModuleChartSankeySchema = z.object({
  id: z.string(),
  type: z.literal('sankey'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartSankeyDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show flow volumes between source → intermediate → outcome nodes (attribution, conversion paths).");

export const ModuleChartSunburstNodeSchema: z.ZodType<{
  name: string;
  value?: number;
  color?: string;
  children?: Array<{ name: string; value?: number; color?: string; children?: any }>;
}> = z.object({
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Node name shown on its ring segment and in the tooltip. One or two words - ring text is drawn " +
    "inside the wedge and is clipped when the wedge is narrow."
  ).meta({ translatable: true }),
  value: z.number().optional(),
  color: z.string().optional(),
  children: z.lazy(() => z.array(ModuleChartSunburstNodeSchema)).optional(),
});

export const ModuleChartSunburstDataSchema = ModuleChartSunburstNodeSchema;

export const ModuleChartSunburstSchema = z.object({
  id: z.string(),
  type: z.literal('sunburst'),
  title: z.string().meta({ translatable: true }),
  data: ModuleChartSunburstDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe("Show hierarchical proportions as concentric radial rings — like a treemap on a pie.");

export const ModuleProgressStageSchema = z.object({
  id: z.string().describe("Stable per-stage id — used for hover/selection sync."),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Short 1-2 word stage name (e.g. 'Build'). It is drawn inside the chevron, which is one of 3-8 " +
    "sharing the module width."
  ).meta({ translatable: true }),
  detail: z.string().max(LABEL_MAX_CHARS.cell).optional().describe(
    "Optional secondary line rendered under the chevron (e.g. 'avg 3m 12s', '94% pass'). It only has " +
    "the width of its own chevron."
  ).meta({ translatable: true }),
  status: ProcessStatusSchema.optional().describe(
    "Legacy: lifecycle status. Prefer `categoryId` with `data.categories` for free-form categorization + palette colors."
  ),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
});

export const ModuleProgressDataSchema = z.object({
  stages: z.array(ModuleProgressStageSchema).describe(
    "3-8 sequential stages of a workflow. Each rendered as a right-pointing chevron; " +
    "colors are driven by the stage's `categoryId` (free-form) or legacy `status` (done/active/pending)."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories replacing the legacy done/active/pending enum. Each stage references " +
    "one via `categoryId`. Colors — use the handyout palette slots 'var(--chart-1)' through 'var(--chart-5)'."
  ),
});

export const ModuleProgressStagesSchema = z.object({
  id: z.string(),
  type: z.literal('progress-stages'),
  title: z.string().meta({ translatable: true }),
  data: ModuleProgressDataSchema.optional(),
  showLegend: z.boolean().optional().describe(
    "Show a legend row with chevron markers + per-status counts. Default true."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-8 workflow stages as sequential right-pointing chevrons with done/active/pending status. " +
  "Best for CI/CD, deployment, or approval workflows."
);

export const ModuleForkJoinNodeSchema = z.object({
  id: z.string().describe("Stable per-node id — used as edge source/target."),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Short node name. It is drawn next to the node at its x/y position, where a long name runs into " +
    "the neighbouring nodes and edges."
  ).meta({ translatable: true }),
  x: z.number().describe("Horizontal position 0..100 (percentage of chart width)."),
  y: z.number().describe(
    "Logical row: 1 = center track, values < 1 shift up, > 1 shift down. E.g. 0.2 and 1.8 for parallel branches around center."
  ),
  status: ProcessStatusSchema.optional().describe(
    "Legacy: lifecycle status. Prefer `categoryId` with `data.categories` for free-form categorization + palette colors."
  ),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
  type: z.enum(['junction']).optional().describe(
    "Set to 'junction' to render this node as a rotated diamond (fork/join point) instead of a normal circle."
  ),
});

export const ModuleForkJoinEdgeSchema = z.tuple([z.string(), z.string()]).describe(
  "[fromId, toId] pair. Edges from a node into a 'pending' node render dashed to signal not-yet-executed."
);

export const ModuleForkJoinDataSchema = z.object({
  nodes: z.array(ModuleForkJoinNodeSchema).describe(
    "All nodes in the graph. Junction nodes (fork/join) declare type: 'junction'."
  ),
  edges: z.array(ModuleForkJoinEdgeSchema).describe(
    "Directed edges connecting node ids."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories replacing legacy done/active/pending. Each node references one via `categoryId`."
  ),
});

export const ModuleForkJoinSchema = z.object({
  id: z.string(),
  type: z.literal('fork-join'),
  title: z.string().meta({ translatable: true }),
  data: ModuleForkJoinDataSchema.optional(),
  showLegend: z.boolean().optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a parallel workflow with fork and join junctions. Nodes have absolute x/y coordinates; " +
  "diamond-shaped junctions mark where flow splits or merges. Best for parallel data processing, " +
  "map/reduce style pipelines, or multi-track workflows."
);

export const ModuleGanttPhaseSchema = z.object({
  id: z.string(),
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Phase name shown as the row label. It sits in the narrow left column next to the bars, so one " +
    "or two words."
  ).meta({ translatable: true }),
  start: z.number().describe("Start day (0-indexed) of the phase on the timeline."),
  duration: z.number().describe("Duration of the phase in days."),
  progress: z.number().min(0).max(100).describe("Completion percentage 0..100."),
  color: z.string().describe(
    "Base color of the phase bar, a handyout palette slot 'var(--chart-1)' through 'var(--chart-5)'."
  ),
});

export const ModuleGanttPhasesDataSchema = z.object({
  phases: z.array(ModuleGanttPhaseSchema).describe(
    "3-10 phases. Rows render in this order top-to-bottom; phases can overlap on the day axis."
  ),
  totalDays: z.number().optional().describe(
    "Total day range shown on the x-axis. Defaults to 45."
  ),
  currentDay: z.number().optional().describe(
    "Optional vertical NOW indicator at this day value."
  ),
  dayLabels: z.array(z.number()).optional().describe(
    "X-axis tick day values. Defaults to [0, 7, 14, 21, 28, 35, 42]."
  ),
});

export const ModuleGanttPhasesSchema = z.object({
  id: z.string(),
  type: z.literal('gantt-phases'),
  title: z.string().meta({ translatable: true }),
  data: ModuleGanttPhasesDataSchema.optional(),
  showLegend: z.boolean().optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-10 project phases as horizontal Gantt bars with per-phase progress fill and an optional " +
  "vertical NOW line. Best for roadmaps or project planning."
);

export const ModuleParallelStreamStepSchema = z.object({
  id: ElementIdField,
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Step name drawn at the step's marker on the track. One or two words - steps sit close together " +
    "on the same line and long names collide."
  ).meta({ translatable: true }),
  pos: z.number().describe("Horizontal position 0..100 along the track."),
  status: ProcessStatusSchema.optional().describe(
    "Legacy: lifecycle status. Prefer `categoryId` with `data.categories`."
  ),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
});

export const ModuleParallelStreamTrackSchema = z.object({
  id: z.string(),
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Track/lane name shown at the left. It sits in the narrow gutter in front of the track line, so " +
    "one or two words."
  ).meta({ translatable: true }),
  color: z.string().describe("Track color."),
  steps: z.array(ModuleParallelStreamStepSchema).describe(
    "Steps along the track, positioned by `pos`. Line fills up to the last done/active step."
  ),
});

export const ModuleParallelStreamMilestoneSchema = z.object({
  id: ElementIdField,
  pos: z.number().describe("Horizontal position 0..100."),
  label: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "Milestone marker label (e.g. 'M1', 'Beta'). It is drawn at the top of a vertical line crossing " +
    "every track, so one or two words."
  ).meta({ translatable: true }),
  subLabel: z.string().max(LABEL_MAX_CHARS.caption).optional().describe(
    "Optional second line under the marker label, in the same narrow slot."
  ).meta({ translatable: true }),
});

export const ModuleParallelStreamsDataSchema = z.object({
  tracks: z.array(ModuleParallelStreamTrackSchema).describe(
    "2-4 parallel tracks/workstreams, each with its own color and step sequence."
  ),
  milestones: z.array(ModuleParallelStreamMilestoneSchema).optional().describe(
    "Optional vertical milestone markers spanning all tracks (e.g. release cuts, phase gates)."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories replacing legacy done/active/pending on step-level."
  ),
});

export const ModuleParallelStreamsSchema = z.object({
  id: z.string(),
  type: z.literal('parallel-streams'),
  title: z.string().meta({ translatable: true }),
  data: ModuleParallelStreamsDataSchema.optional(),
  showLegend: z.boolean().optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 2-4 parallel workstreams/tracks over a shared horizontal axis, each with its own step " +
  "sequence and shared milestone markers. Best for multi-workstream product plans."
);

export const ModuleSegmentedProgressSegmentSchema = z.object({
  id: z.string(),
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Segment name shown on the bar and in the legend. One or two words - the segment only gets its " +
    "own share of the bar width."
  ).meta({ translatable: true }),
  weight: z.number().describe(
    "Relative weight — segment width is weight / sum(weights)."
  ),
  progress: z.number().min(0).max(100).describe("Completion percentage 0..100."),
  color: z.string(),
});

export const ModuleSegmentedProgressDataSchema = z.object({
  segments: z.array(ModuleSegmentedProgressSegmentSchema).describe(
    "3-6 sequential segments/phases. Widths are proportional to `weight`; fill is per-segment `progress`."
  ),
});

export const ModuleSegmentedProgressSchema = z.object({
  id: z.string(),
  type: z.literal('segmented-progress'),
  title: z.string().meta({ translatable: true }),
  data: ModuleSegmentedProgressDataSchema.optional(),
  showLegend: z.boolean().optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-6 weighted phases as a single segmented progress bar with per-segment fill percentage. " +
  "Segment widths reflect relative weight; use for effort/budget-weighted milestones."
);

export const ModuleStateMachineNodeSchema = z.object({
  id: z.string(),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "State name drawn at the state circle. One or two words - a long name runs into the neighbouring " +
    "states and their transition arrows."
  ).meta({ translatable: true }),
  x: z.number().describe("Horizontal position 0..100."),
  y: z.number().describe("Logical row (1..N)."),
  status: ProcessStatusSchema.optional().describe(
    "Legacy: lifecycle status. Prefer `categoryId` with `data.categories`."
  ),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
});

export const ModuleStateMachineTransitionTypeSchema = z.enum(['normal', 'negative', 'retry']).describe(
  "Edge kind: 'normal' (solid), 'negative' (dashed, e.g. reject/error branch), 'retry' (dotted loop-back curve)."
);

export const ModuleStateMachineTransitionSchema = z.object({
  id: ElementIdField,
  from: z.string().describe(
    "Source state of the transition, matches a `ModuleStateMachineNode.id` in `data.states`."
  ),
  to: z.string().describe(
    "Target state of the transition, matches a `ModuleStateMachineNode.id` in `data.states`."
  ),
  label: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "Transition name (e.g. 'publish', 'reject'). A single word where possible - it is drawn on the " +
    "edge itself, between two states."
  ).meta({ translatable: true }),
  type: ModuleStateMachineTransitionTypeSchema.optional().describe(
    "Legacy: edge visual style. Prefer `transitionCategoryId` with `data.transitionCategories` for free-form."
  ),
  transitionCategoryId: z.string().optional().describe(
    "Optional transition-category id, matches a `ModuleCategory.id` in `data.transitionCategories`."
  ),
});

export const ModuleStateMachineDataSchema = z.object({
  states: z.array(ModuleStateMachineNodeSchema).describe(
    "States rendered as circles at absolute x/y positions."
  ),
  transitions: z.array(ModuleStateMachineTransitionSchema).describe(
    "Directed transitions with labels; edge style is driven by `type` or `transitionCategoryId`."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories for STATES (replaces legacy done/active/pending)."
  ),
  transitionCategories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories for TRANSITIONS (replaces legacy normal/negative/retry)."
  ),
});

export const ModuleStateMachineSchema = z.object({
  id: z.string(),
  type: z.literal('state-machine'),
  title: z.string().meta({ translatable: true }),
  data: ModuleStateMachineDataSchema.optional(),
  showLegend: z.boolean().optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a state machine with labeled transitions. Circle-shaped states; solid/dashed/dotted edges " +
  "differentiate normal, negative, and retry paths. Best for approval flows, document lifecycles, " +
  "or protocol diagrams."
);

export const ModuleTimelineStepperStepSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Step name shown at its dot on the line. One or two words - 4-8 steps share the module width."
  ).meta({ translatable: true }),
  status: ProcessStatusSchema.optional().describe(
    "Legacy: lifecycle status. Prefer `categoryId` with `data.categories`."
  ),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
  date: z.string().describe(
    "Free-form date label (e.g. 'Sep 01', '2024 Q1'). Rendered verbatim."
  ).meta({ translatable: true }),
  duration: z.string().optional().describe(
    "Optional duration label (e.g. '4d')."
  ).meta({ translatable: true }),
});

export const ModuleTimelineStepperDataSchema = z.object({
  steps: z.array(ModuleTimelineStepperStepSchema).describe(
    "4-8 steps arranged left-to-right on a single horizontal line. When any step has `status` " +
    "or `categoryId`, the progress line fills up to the first `active`/highlighted step and a legend is shown."
  ),
  initialSelectedIndex: z.number().int().optional().describe(
    "Optional initial selection. Omit it and the module starts with nothing selected, which is " +
    "almost always right: a preselected step claims a legend row and a marker ring the reader " +
    "never asked for. Set it only to open the module on one specific step."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories replacing legacy done/active/pending."
  ),
});

export const ModuleTimelineStepperSchema = z.object({
  id: z.string(),
  type: z.literal('timeline-stepper'),
  title: z.string().meta({ translatable: true }),
  data: ModuleTimelineStepperDataSchema.optional(),
  showLegend: z.boolean().optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 4-8 dated events on a single horizontal line with click-to-select. Supports both grouped mode " +
  "(status per step, status legend, progress line filling) and ungrouped mode (plain sequence, no status)."
);

export const ModuleQuadrantMatrixItemSchema = z.object({
  id: z.string(),
  name: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Item name shown as the title of the dot's tooltip; it is not drawn on the plot. One or two words " +
    "keep the tooltip compact."
  ).meta({ translatable: true }),
  effort: z.number().min(0).max(100).describe("Horizontal position 0..100."),
  impact: z.number().min(0).max(100).describe("Vertical position 0..100."),
});

export const ModuleQuadrantMatrixDataSchema = z.object({
  items: z.array(ModuleQuadrantMatrixItemSchema).describe(
    "Items scattered across a 2x2 grid based on effort (x) and impact (y). Quadrant is auto-derived: " +
    "impact≥50+effort<50 = quick wins, impact≥50+effort≥50 = big bets, impact<50+effort<50 = fill-ins, " +
    "impact<50+effort≥50 = time sinks."
  ),
  xLabel: z.string().max(LABEL_MAX_CHARS.axisTick).optional().describe(
    "X-axis label, drawn on a single line under the plot. A noun phrase, not a sentence. Defaults to 'Effort'."
  ).meta({ translatable: true }),
  yLabel: z.string().max(LABEL_MAX_CHARS.axisTick).optional().describe(
    "Y-axis label, drawn on a single line beside the plot. A noun phrase, not a sentence. Defaults to 'Impact'."
  ).meta({ translatable: true }),
});

export const ModuleQuadrantMatrixSchema = z.object({
  id: z.string(),
  type: z.literal('quadrant-matrix'),
  title: z.string().meta({ translatable: true }),
  data: ModuleQuadrantMatrixDataSchema.optional(),
  showLegend: z.boolean().optional(),
  view3d: z.boolean().optional().describe(
    "Isometric 3D view. Default false: the flat 2x2 map. When true the map opens as an isometric " +
    "floor of tinted quadrant tiles seen from a fixed angle, with every item standing on it as a pillar " +
    "whose height is its impact relative to the visible impact range, and a 2D/3D toggle that morphs " +
    "back into the flat map. Only for a striking visual of which items stand out; never when the " +
    "audience must read exact effort and impact scores. Maps with more than 600 items always render " +
    "flat without the toggle."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 5-30 items as dots scattered across a 2x2 effort/impact map. Auto-classifies each dot into " +
  "quick-wins / big-bets / fill-ins / time-sinks quadrants. Best for prioritization, backlog triage, " +
  "or roadmap planning."
);

export const ModuleCorrelationHeatmapDataSchema = z.object({
  features: z.array(z.string().max(LABEL_MAX_CHARS.axisNoWrap).meta({ translatable: true })).describe(
    "N short feature labels — used as both row and column labels of the symmetric N×N matrix. " +
    "They are drawn as axis text on a single line and cannot wrap."
  ),
  matrix: z.array(z.array(z.number())).describe(
    "Square N×N correlation matrix. Values −1..1. Length must match features.length. " +
    "The diagonal is typically 1 (feature correlated with itself)."
  ),
});

export const ModuleCorrelationHeatmapSchema = z.object({
  id: z.string(),
  type: z.literal('correlation-heatmap'),
  title: z.string().meta({ translatable: true }),
  data: ModuleCorrelationHeatmapDataSchema.optional(),
  view3d: z.boolean().optional().describe(
    "Isometric 3D view. Default false: the flat matrix. When true the matrix opens as isometric 3D " +
    "boxes seen from a fixed angle whose height is the strength of the correlation (negative cells keep " +
    "their amber tint, the diagonal stays flat), with a 2D/3D toggle that morphs back into the flat " +
    "matrix. Only for a striking visual of which pairs stand out; never when the audience must read " +
    "exact coefficients. Matrices with more than 20 features always render flat without the toggle."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a symmetric N×N correlation matrix as a heatmap. Positive correlations render as grayscale " +
  "brightness, negative as amber tint. Hovering highlights the full row + column. Best for showing " +
  "how features/variables interrelate."
);

export const ModuleFeatureComparisonRowSchema = z.object({
  id: ElementIdField,
  name: z.string().max(LABEL_MAX_CHARS.row).describe(
    "Feature/row name (left column). Every row has the same fixed height: the name wraps to at most " +
    "two lines and is clipped after that, so keep it short."
  ).meta({ translatable: true }),
  values: z.array(z.union([z.boolean(), z.string().meta({ translatable: true })])).describe(
    "One value per plan column. true = checkmark, false = dash, string = literal value (e.g. '5 GB', '99.99%'). " +
    "Strings are clipped after two lines."
  ),
});

export const ModuleFeatureComparisonPlanSchema = z.object({
  name: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Plan/column name (e.g. 'Free', 'Pro'). It heads one of 2-5 columns sharing the module width."
  ).meta({ translatable: true }),
  price: z.string().optional().describe("Optional price line under the plan name (e.g. '€29/mo')."),
  highlight: z.boolean().optional().describe(
    "When true, the column gets a subtle background tint to draw attention (e.g. the recommended plan)."
  ),
});

export const ModuleFeatureComparisonDataSchema = z.object({
  features: z.array(ModuleFeatureComparisonRowSchema).describe(
    "5-15 feature rows. Each row's `values` array length must match `plans.length`. The module shows a " +
    "fixed number of rows per page with a pager below, so long lists stay in one module."
  ),
  plans: z.array(ModuleFeatureComparisonPlanSchema).describe(
    "2-5 plan columns rendered left-to-right."
  ),
});

export const ModuleFeatureComparisonSchema = z.object({
  id: z.string(),
  type: z.literal('feature-comparison'),
  title: z.string().meta({ translatable: true }),
  data: ModuleFeatureComparisonDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show features × plans as a grid with boolean checkmarks or literal string values. Hovering a cell " +
  "highlights the full row + column and shows a detail line above the grid. More rows than fit one " +
  "page are paged inside the module (previous/next pager), never split across viewer pages. Best for pricing pages and " +
  "product tier comparisons. For any other tabular data (named rows against text, number, euro or " +
  "percent columns) use `table` instead."
);

export const VALUE_FORMATS = ['number', 'eur', 'percent'] as const;

export const ValueFormatSchema = z.enum(VALUE_FORMATS).describe(
  "How a raw number is printed. 'number' = plain number with thousands separators, 'eur' = euro " +
  "amount without cents, 'percent' = the number is already in percent units (82 means 82 %), never a " +
  "fraction. Default 'number'."
);

const DigitsField = z.number().int().min(0).max(3).optional().describe(
  "Decimal places to print. Default 0 (percent values below 10 with a fraction get 1)."
);

const UnitField = z.string().max(8).optional().describe(
  "Short unit printed after the number, e.g. 'min', 'ms', 'of 5'. Leave unset for euro and percent " +
  "values, their sign comes from `format`."
).meta({ translatable: true });

export const RING_KPIS_MAX_ITEMS = 12;
export const RING_KPIS_TWO_ROW_THRESHOLD = 4;

export const ModuleRingKpiItemSchema = z.object({
  id: ElementIdField,
  label: z.string().max(SINGLE_LINE_MAX_CHARS.tile).describe(
    "KPI name above the ring. One line in a tile about 13rem wide; longer text is clipped, so a " +
    "few short words."
  ).meta({ translatable: true }),
  value: z.number().describe("The KPI's current value as a raw number, formatted by `format`."),
  format: ValueFormatSchema.optional(),
  digits: DigitsField,
  unit: UnitField,
  max: z.number().positive().optional().describe(
    "Upper bound of the scale. When set and `progress` is not, the ring fills value / max."
  ),
  progress: z.number().min(0).max(100).optional().describe(
    "Explicit ring fill from 0 to 100. Use it when the KPI has no natural maximum (response time " +
    "against a target). Takes precedence over `max`."
  ),
  previous: z.number().optional().describe(
    "Value of the comparison period. Fill it whenever the slide shows one (last month, last year, " +
    "target period). The tile then prints the percentage change (value / previous - 1) with an " +
    "up or down arrow, coloured green when good and amber when bad, followed by `deltaPeriod`, and " +
    "under it `previousLabel` with this value. Leave unset when the slide has no comparison; a 0 " +
    "shows no change."
  ),
  invertDelta: z.boolean().optional().describe(
    "true when a decrease is good for this KPI, e.g. response time, error rate, churn, cost. Flips " +
    "the green / amber colouring of the change and the ring. Leave unset when an increase is good."
  ),
});

export const ModuleRingKpisDataSchema = z.object({
  items: z.array(ModuleRingKpiItemSchema).min(1).max(RING_KPIS_MAX_ITEMS).describe(
    "1-12 KPI tiles, each with a segmented progress ring. The grid never has more than two rows: up " +
    "to 3 tiles form one row, from 4 tiles on they flow into two rows with as many columns as needed " +
    "(2+2, 3+2, 3+3, ... 6+6). Each tile is at least 13rem wide; when the tiles do not fit the " +
    "module width the row scrolls sideways, so 2-6 tiles read best."
  ),
  deltaPeriod: z.string().max(SINGLE_LINE_MAX_CHARS.label).optional().describe(
    "Written-out comparison printed after every percentage change, e.g. 'vs last month' or " +
    "'vs last year'. Fill it whenever items carry `previous`; take the wording from the slide."
  ).meta({ translatable: true }),
  previousLabel: z.string().max(SINGLE_LINE_MAX_CHARS.label).optional().describe(
    "Caption in front of every `previous` value, e.g. 'Last month'. Defaults to 'Previous'."
  ).meta({ translatable: true }),
});

export const ModuleRingKpisSchema = z.object({
  id: z.string(),
  type: z.literal('ring-kpis'),
  title: z.string().meta({ translatable: true }),
  data: ModuleRingKpisDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "A grid of 1-12 KPI tiles in at most two rows (one row up to 3 tiles, otherwise two rows with " +
  "unlimited columns that scroll sideways). Each tile shows a label, a segmented progress ring and " +
  "the formatted value, and with a `previous` value the percentage change against the comparison " +
  "period plus the previous value. Best for utilisation, satisfaction or quota KPIs that have a " +
  "natural 0-100 % fill."
);

export const METRIC_TABS_MAX_METRICS = 4;

export const ModuleMetricTabSchema = z.object({
  id: ElementIdField,
  label: z.string().max(SINGLE_LINE_MAX_CHARS.label).describe(
    "Metric name on its tab. Up to four tabs share the module width, so one or two words."
  ).meta({ translatable: true }),
  values: z.array(z.number()).min(2).max(24).describe(
    "One value per entry of `data.periods`, oldest first. Must have the same length as `periods`."
  ),
  compareValues: z.array(z.number()).min(2).max(24).optional().describe(
    "Optional comparison series (e.g. the previous year), same length as `values`. Drawn as a dashed line."
  ),
  aggregate: z.enum(['sum', 'average', 'last']).optional().describe(
    "How the series rolls up into the number on the tab. 'sum' for amounts and counts, 'average' for " +
    "rates and percentages, 'last' for stock values. Default 'sum'."
  ),
  format: ValueFormatSchema.optional(),
  digits: DigitsField,
  unit: UnitField,
});

export const ModuleMetricTabsChartDataSchema = z.object({
  periods: z.array(z.string().max(LABEL_MAX_CHARS.axisTick).meta({ translatable: true })).min(2).max(24).describe(
    "X-axis labels, oldest first ('Jan', 'Q1', 'Week 3'). Every metric's `values` follow this grid."
  ),
  metrics: z.array(ModuleMetricTabSchema).min(1).max(METRIC_TABS_MAX_METRICS).describe(
    "1-4 metrics. Each becomes a tab showing its rolled-up value; the selected tab's series is charted."
  ),
  seriesLabel: z.string().max(LABEL_MAX_CHARS.legend).optional().describe(
    "Legend label of the solid current series. Defaults to the selected metric's label."
  ).meta({ translatable: true }),
  compareLabel: z.string().max(LABEL_MAX_CHARS.legend).optional().describe(
    "Legend and tooltip label of the dashed comparison series, e.g. 'Previous year'. Defaults to 'Comparison'."
  ).meta({ translatable: true }),
});

export const ModuleMetricTabsChartSchema = z.object({
  id: z.string(),
  type: z.literal('metric-tabs-chart'),
  title: z.string().meta({ translatable: true }),
  data: ModuleMetricTabsChartDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Tabs with 1-4 headline metrics above one area chart. Selecting a tab charts that metric over the " +
  "periods, optionally against a dashed comparison series. Best for a dashboard of related KPIs that " +
  "share one time axis."
);

export const BREAKDOWN_MAX_ITEMS = 8;

export const ModuleBreakdownItemSchema = z.object({
  id: ElementIdField,
  name: z.string().max(SINGLE_LINE_MAX_CHARS.row).describe(
    "Name of the part. One line above its bar, shared with the value on the right; longer text is clipped."
  ).meta({ translatable: true }),
  value: z.number().min(0).describe("The part's value as a raw number, formatted by `data.format`."),
});

export const ModuleBreakdownDataSchema = z.object({
  label: z.string().max(LABEL_MAX_CHARS.row).optional().describe(
    "Small caption above the total, e.g. 'Revenue by product line, 2026'."
  ).meta({ translatable: true }),
  total: z.number().optional().describe(
    "Headline total. Defaults to the sum of the items; set it when the items are only the largest parts."
  ),
  format: ValueFormatSchema.optional(),
  items: z.array(ModuleBreakdownItemSchema).min(2).max(BREAKDOWN_MAX_ITEMS).describe(
    "2-8 parts, largest first. Each gets a hatched bar sized against the total."
  ),
  footnote: z.string().max(LABEL_MAX_CHARS.row * 2).optional().describe(
    "Optional sentence under the bars, e.g. the source or what is excluded."
  ).meta({ translatable: true }),
});

export const ModuleBreakdownSchema = z.object({
  id: z.string(),
  type: z.literal('breakdown'),
  title: z.string().meta({ translatable: true }),
  data: ModuleBreakdownDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "A big total with its parts listed underneath as hatched horizontal bars. Selecting a part puts its " +
  "value and share of the total into the headline. Best for revenue, cost or headcount split into 2-8 parts " +
  "when the amounts matter; use `pie` when only the shares matter."
);

export const ModuleDualMetricDetailSchema = z.object({
  label: z.string().max(SINGLE_LINE_MAX_CHARS.label).describe(
    "Caption of the supporting fact, e.g. 'Largest driver'."
  ).meta({ translatable: true }),
  value: z.string().max(LABEL_MAX_CHARS.label).describe(
    "The supporting fact itself, e.g. 'Network operations, 61 %'."
  ).meta({ translatable: true }),
  description: z.string().max(LABEL_MAX_CHARS.row * 2).optional().describe(
    "Optional explanatory sentence printed under the fact."
  ).meta({ translatable: true }),
});

export const ModuleDualMetricItemSchema = z.object({
  id: ElementIdField,
  label: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Name of the metric, printed under its value."
  ).meta({ translatable: true }),
  value: z.number().describe("The metric's value as a raw number, formatted by `format`."),
  format: ValueFormatSchema.optional(),
  progress: z.number().min(0).max(100).optional().describe(
    "Meter fill from 0 to 100. Defaults to this metric's share of the two metrics' sum."
  ),
  showShare: z.boolean().optional().describe(
    "Show this metric's share of the two metrics' sum as a small badge next to the value. Default true."
  ),
  detail: ModuleDualMetricDetailSchema.optional(),
});

export const ModuleDualMetricPeriodSchema = z.object({
  id: ElementIdField,
  label: z.string().max(CONTENT_LABEL_BUDGET_CH.caption).describe(
    "Period name on the switcher, e.g. '2026' or 'Target 2030'. Short, the switcher sits in one row."
  ).meta({ translatable: true }),
  metrics: z.array(ModuleDualMetricItemSchema).length(2).describe(
    "Exactly two metrics that together make up a whole, in the same order in every period."
  ),
});

export const ModuleDualMetricDataSchema = z.object({
  label: z.string().max(LABEL_MAX_CHARS.row).optional().describe(
    "Small caption above the metrics, e.g. 'Revenue mix'."
  ).meta({ translatable: true }),
  periods: z.array(ModuleDualMetricPeriodSchema).min(1).max(4).describe(
    "1-4 periods. One period shows no switcher; several show a period switcher."
  ),
});

export const ModuleDualMetricSchema = z.object({
  id: z.string(),
  type: z.literal('dual-metric'),
  title: z.string().meta({ translatable: true }),
  data: ModuleDualMetricDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Two complementary metrics side by side (recurring vs. project revenue, new vs. returning customers), " +
  "each with a large value, its share of the whole and a tick meter, switchable across up to four periods."
);

export const STAGE_BARS_MAX_STAGES = 7;

export const ModuleStageSchema = z.object({
  id: ElementIdField,
  name: z.string().max(SINGLE_LINE_MAX_CHARS.label).describe(
    "Stage name in the narrow left column. One line, clipped beyond about 12 characters on a phone."
  ).meta({ translatable: true }),
  value: z.number().min(0).describe("Count or amount that reached this stage, formatted by `data.format`."),
});

export const ModuleStageRangeSchema = z.object({
  id: ElementIdField,
  label: z.string().max(CONTENT_LABEL_BUDGET_CH.caption).describe(
    "Range name on the switcher, e.g. 'Q3 2026'. Short, the switcher sits in one row."
  ).meta({ translatable: true }),
  stages: z.array(ModuleStageSchema).min(2).max(STAGE_BARS_MAX_STAGES).describe(
    "2-7 stages, top of the funnel first. Use the same stage names in every range."
  ),
});

export const ModuleStageBarsDataSchema = z.object({
  label: z.string().max(LABEL_MAX_CHARS.row).optional().describe(
    "Small caption above the headline number, e.g. 'Sales pipeline'."
  ).meta({ translatable: true }),
  format: ValueFormatSchema.optional(),
  ranges: z.array(ModuleStageRangeSchema).min(1).max(4).describe(
    "1-4 time ranges. One range shows no switcher; several show a range switcher."
  ),
  legendTitle: z.string().max(LABEL_MAX_CHARS.row).optional().describe(
    "Heading of the legend under the bars, which lists each stage with its conversion from the " +
    "previous stage. Defaults to 'Conversion from previous stage'."
  ).meta({ translatable: true }),
});

export const ModuleStageBarsSchema = z.object({
  id: z.string(),
  type: z.literal('stage-bars'),
  title: z.string().meta({ translatable: true }),
  data: ModuleStageBarsDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "A funnel drawn as monochrome horizontal bars, each sized against the first stage, with the stage " +
  "value and its share of the first stage. A legend underneath lists the step-to-step conversion. Best " +
  "for sales pipelines, sign-up flows and other stage drop-offs, especially when several time ranges " +
  "are compared; `funnel` is the single-range chart alternative."
);

export const INSIGHT_PANEL_MAX_ITEMS = 5;

export const ModuleInsightItemSchema = z.object({
  id: ElementIdField,
  value: z.union([z.number(), z.string().max(12)]).describe(
    "The big number. A raw number is formatted by `format`; a short string (max 12 characters, e.g. " +
    "'3x', 'Q2') is printed as written."
  ),
  format: ValueFormatSchema.optional(),
  digits: DigitsField,
  unit: UnitField,
  headline: z.string().max(LABEL_MAX_CHARS.row * 2).describe(
    "One-sentence takeaway under the number."
  ).meta({ translatable: true }),
  description: z.string().max(LABEL_MAX_CHARS.row * 4).optional().describe(
    "Optional supporting sentence with the concrete detail behind the takeaway."
  ).meta({ translatable: true }),
});

export const ModuleInsightPanelDataSchema = z.object({
  badge: z.string().max(SINGLE_LINE_MAX_CHARS.label).optional().describe(
    "Pill label in the top left corner. Defaults to 'Key insights'."
  ).meta({ translatable: true }),
  items: z.array(ModuleInsightItemSchema).min(1).max(INSIGHT_PANEL_MAX_ITEMS).describe(
    "1-5 insights. With more than one, the panel advances through them automatically."
  ),
});

export const ModuleInsightPanelSchema = z.object({
  id: z.string(),
  type: z.literal('insight-panel'),
  title: z.string().meta({ translatable: true }),
  data: ModuleInsightPanelDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "A large gradient panel that presents 1-5 headline numbers one at a time, each with a one-sentence " +
  "takeaway, advancing automatically. Best for the few figures a reader must remember. Use " +
  "`big-number-chart` for one number with its trend over time instead."
);

export const DATA_TABLE_MAX_COLUMNS = 6;
export const DATA_TABLE_MAX_ROWS = 60;

export const DataTableColumnFormatSchema = z.enum(['text', 'number', 'eur', 'percent', 'tag']).describe(
  "How the column's cells are printed. 'text' as written, 'number' / 'eur' / 'percent' as formatted " +
  "numbers (percent cells are in percent units, 12.5 means 12.5 %), 'tag' as a short category with a " +
  "colored dot (same text, same color). Default 'text'."
);

export const ModuleDataTableColumnSchema = z.object({
  id: ElementIdField,
  label: z.string().max(SINGLE_LINE_MAX_CHARS.label).describe(
    "Column header. One line, clipped when the column is narrow."
  ).meta({ translatable: true }),
  format: DataTableColumnFormatSchema.optional(),
  digits: DigitsField,
  align: z.enum(['left', 'right']).optional().describe(
    "Cell alignment. Defaults to right for number, eur and percent columns, left otherwise."
  ),
});

export const ModuleDataTableRowSchema = z.object({
  id: ElementIdField,
  cells: z.array(z.union([z.string().max(SINGLE_LINE_MAX_CHARS.row).meta({ translatable: true }), z.number()])).describe(
    "One cell per column, in column order. Numbers for number/eur/percent columns, short text otherwise. " +
    "Cells never wrap; longer text is clipped."
  ),
});

export const ModuleDataTableDataSchema = z.object({
  columns: z.array(ModuleDataTableColumnSchema).min(2).max(DATA_TABLE_MAX_COLUMNS).describe(
    "2-6 columns. The first column names the row (customer, product) and is always visible."
  ),
  rows: z.array(ModuleDataTableRowSchema).min(1).max(DATA_TABLE_MAX_ROWS).describe(
    "1-60 rows in their initial display order. Readers can search the text columns, sort by any column " +
    "and page through the rows, so a table may hold far more rows than fit one page."
  ),
});

export const ModuleDataTableSchema = z.object({
  id: z.string(),
  type: z.literal('table'),
  title: z.string().meta({ translatable: true }),
  data: ModuleDataTableDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "A plain data table: named rows (customers, products, sites) against 2-6 typed columns of text, " +
  "numbers, euro amounts, percentages or category tags. Readers get a search field over the text " +
  "columns, sortable column headers, a column menu to show or hide columns and a pager with a fixed " +
  "number of rows per page, so the module keeps one height and rows beyond the first page are paged " +
  "inside it. Best for lists a reader looks values up in, and the default for a table on the slide. For " +
  "plans compared feature by feature with checkmarks use `feature-comparison` instead."
);

export const ModuleSkillLevelMatrixDataSchema = z.object({
  rowLabels: z.array(z.string().max(LABEL_MAX_CHARS.axisNoWrap).meta({ translatable: true })).describe(
    "Row labels — typically skill or capability names. Drawn on a single line, so keep them short."
  ),
  colLabels: z.array(z.string().max(LABEL_MAX_CHARS.axisNoWrap)).describe(
    "Column labels — typically people or team names. Drawn on a single line, so keep them short."
  ),
  levels: z.array(z.array(z.number())).describe(
    "2D matrix of proficiency levels sized rowLabels.length × colLabels.length. " +
    "Values 0..maxLevel. Each cell renders as filled/empty dots."
  ),
  maxLevel: z.number().int().min(1).max(10).optional().describe(
    "Maximum level (number of dots per cell). Default 5."
  ),
});

export const ModuleSkillLevelMatrixSchema = z.object({
  id: z.string(),
  type: z.literal('skill-level-matrix'),
  title: z.string().meta({ translatable: true }),
  data: ModuleSkillLevelMatrixDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a rows × columns proficiency matrix with per-cell filled/empty dots representing skill level. " +
  "Row and column averages are shown alongside. Best for team skill maps, capability audits, " +
  "training gap analysis."
);

export const ModuleCyclePhaseSchema = z.object({
  id: z.string().describe("Stable per-phase id — used for hover/selection sync."),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Short 1-2 word phase name (e.g. 'Discover', 'Design'). It is drawn at its node on the ring and " +
    "repeated in the legend."
  ).meta({ translatable: true }),
  status: ProcessStatusSchema,
  duration: z.string().describe("Free-form duration label (e.g. '3d', '2w'). Shown in the legend value.").meta({ translatable: true }),
});

export const ModuleCycleDataSchema = z.object({
  phases: z.array(ModuleCyclePhaseSchema).describe(
    "3-8 sequential phases arranged in a loop. Each phase carries a status (done/active/pending) and a duration label."
  ),
});

export const ModuleNodeCycleSchema = z.object({
  id: z.string(),
  type: z.literal('node-cycle'),
  title: z.string().meta({ translatable: true }),
  data: ModuleCycleDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-8 phases as a directional ring of numbered nodes with arrows between them. " +
  "Center label reflects the active or hovered phase; a legend row shows the full sequence with durations. " +
  "Best for iterative processes like PDCA, sprints, or recurring workflows."
);

export const ModuleSegmentedCycleSchema = z.object({
  id: z.string(),
  type: z.literal('segmented-cycle'),
  title: z.string().meta({ translatable: true }),
  data: ModuleCycleDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-8 phases as filled donut segments (done fully, active accented, pending dashed). " +
  "Center label reflects the active or hovered phase; a legend row shows the full sequence with durations. " +
  "Best for time-boxed cycles where relative duration matters."
);

export const ModuleEcosystemSegmentStatusSchema = z.enum(['active', 'inactive']);

export const ModuleEcosystemSegmentSchema = z.object({
  id: z.string().describe("Stable per-segment id."),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Segment display name (rendered as curved text along the wedge). One or two words - a wedge is " +
    "one of up to ten on its ring and the curved text cannot wrap."
  ).meta({ translatable: true }),
  status: ModuleEcosystemSegmentStatusSchema.optional().describe(
    "Legacy binary status. When set + no `categoryId`: 'active' fills the wedge, 'inactive' renders " +
    "as dashed outline. Prefer `categoryId` with `data.categories` for free-form segmentation."
  ),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`."
  ),
});

export const ModuleEcosystemRingSchema = z.object({
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Ring display name (shown as a hover-detail column and in the idle info line). It shares that " +
    "line with the segment name and the per-category counts."
  ).meta({ translatable: true }),
  segments: z.array(ModuleEcosystemSegmentSchema).describe(
    "Wedge segments arranged clockwise starting at 0° (top)."
  ),
});

export const ModuleEcosystemCoreSchema = z.object({
  lines: z.array(z.string().max(LABEL_MAX_CHARS.caption).meta({ translatable: true })).describe(
    "Center-circle label lines — rendered stacked vertically (e.g. ['Platform','Services']). One word " +
    "per line: each line is drawn inside the small center circle and cannot wrap."
  ),
});

export const ModuleEcosystemMarketplaceSchema = z.object({
  label: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "Outer umbrella ring label — rendered as a single curved caption at the top. One or two words: " +
    "the caption follows the arc and cannot wrap."
  ).meta({ translatable: true }),
});

export const ModuleEcosystemMapDataSchema = z.object({
  core: ModuleEcosystemCoreSchema,
  foundation: ModuleEcosystemRingSchema.describe(
    "Inner ring — best with up to ~5 foundational segments."
  ),
  products: ModuleEcosystemRingSchema.describe(
    "Outer ring — best with up to ~10 product segments."
  ),
  marketplace: ModuleEcosystemMarketplaceSchema,
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories replacing the binary active/inactive default. Each wedge is then " +
    "filled with its category color and segments reference them via `categoryId`. When omitted, the " +
    "module auto-generates `[{id:'active'}, {id:'inactive'}]` from the `activeText`/`inactiveText`/" +
    "`activeShort`/`availableShort` props — 'active' renders filled, 'inactive' as a dashed outline."
  ),
});

export const ModuleEcosystemMapSchema = z.object({
  id: z.string(),
  type: z.literal('ecosystem-map'),
  title: z.string().meta({ translatable: true }),
  data: ModuleEcosystemMapDataSchema.optional(),
  activeText: z.string().optional().describe(
    "Info-line text when hovering an active segment. Default 'active'."
  ).meta({ translatable: true }),
  inactiveText: z.string().optional().describe(
    "Info-line text when hovering an inactive segment. Default 'not active'."
  ).meta({ translatable: true }),
  activeShort: z.string().optional().describe(
    "Short suffix next to each ring's active count. Default 'active'."
  ).meta({ translatable: true }),
  availableShort: z.string().optional().describe(
    "Short suffix next to each ring's inactive count. Default 'available'."
  ).meta({ translatable: true }),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a two-ring concentric ecosystem map: a filled core, a foundation ring, a products ring, " +
  "and an outer marketplace umbrella. Segments are marked active (filled) or inactive (dashed outline). " +
  "Best for portfolio overviews, capability maps, or platform surface inventories."
);

export const ModuleFlatHierarchyNodeSchema = z.object({
  id: z.string().describe("Stable per-node id."),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Short node name. It is drawn inside the node box, and every node on a level shares that level's " +
    "column width."
  ).meta({ translatable: true }),
  parent: z.string().nullable().describe("Parent node id, or null for the root."),
  level: z.number().describe("Depth level — 0 for root, children have increasing values."),
  weight: z.number().optional().describe("Optional numeric weight (e.g. headcount)."),
});

const HierarchyNodesSchema = z.array(ModuleFlatHierarchyNodeSchema).describe(
  "Flat list of hierarchy nodes with parent pointers — 3-level structure works best."
);

export const ModuleHierarchyTreeColumnSchema = z.object({
  label: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Column header label. It only has its own column's width, and columns sit side by side."
  ).meta({ translatable: true }),
  levels: z.array(z.number()).describe(
    "Which hierarchy levels this column contains. A column can span multiple levels — " +
    "e.g. [0, 1] merges the root plus its immediate children into one wide column."
  ),
});

export const ModuleHierarchyTreeDataSchema = z.object({
  nodes: HierarchyNodesSchema,
  variant: z.enum(['columnar', 'bracket', 'node-link']).optional().describe(
    "Layout style. 'columnar' = column-per-level cards. 'bracket' = bracket-style dendrogram. " +
    "'node-link' = classic node-link tree. Default: 'columnar'."
  ),
  columns: z.array(ModuleHierarchyTreeColumnSchema).optional().describe(
    "Custom column layout (columnar variant only). Each column groups nodes from the listed " +
    "hierarchy levels. Omit to use one column per level."
  ),
});

export const ModuleHierarchyTreeSchema = z.object({
  id: z.string(),
  type: z.literal('hierarchy-tree'),
  title: z.string().meta({ translatable: true }),
  data: ModuleHierarchyTreeDataSchema.optional(),
  variant: z.enum(['columnar', 'bracket', 'node-link']).optional(),
  columns: z.array(ModuleHierarchyTreeColumnSchema).optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a hierarchy (org chart, taxonomy, product breakdown) in one of three layouts: " +
  "columnar (default, column-per-level with optional column spanning), bracket dendrogram, " +
  "or node-link tree."
);

export const ModuleParallelCategoryTreesSchema = z.object({
  id: z.string(),
  type: z.literal('parallel-category-trees'),
  title: z.string().meta({ translatable: true }),
  data: z.object({
    nodes: HierarchyNodesSchema,
    rootIds: z.tuple([
      z.string().describe(
        "Root of the left subtree, matches a `ModuleFlatHierarchyNode.id` in `data.nodes`."
      ),
      z.string().describe(
        "Root of the right subtree, matches a `ModuleFlatHierarchyNode.id` in `data.nodes`."
      ),
    ]).describe(
      "The two subtree roots to render side-by-side. Both ids must exist in `nodes`."
    ),
  }).optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show two subtrees of a hierarchy side-by-side (e.g. Product vs Engineering departments). " +
  "Best for comparing parallel categories with the same shape."
);

export const ModuleActivityFeedItemSchema = z.object({
  id: z.union([z.string(), z.number()]),
  icon: z.enum([
    'git-commit',
    'pencil',
    'trash-2',
    'check-circle',
    'message-circle',
    'user-plus',
    'plus',
  ]).describe("Icon name — resolved to a Lucide icon inside the module."),
  tint: z.string().describe("Icon tint color — CSS variable string or hex."),
  actor: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "Who did the action (e.g. 'Manu'). Actor, verb and target are printed as one sentence inside a " +
    "single feed cell, so each part stays short."
  ),
  verb: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "What action they took (e.g. 'deployed', 'commented on'). Middle part of the one-line actor/verb/" +
    "target sentence."
  ).meta({ translatable: true }),
  target: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "What was acted on (e.g. a repo, ticket, or file — rendered in mono). Last part of the one-line " +
    "actor/verb/target sentence."
  ),
  detail: z.string().max(LABEL_MAX_CHARS.cell).optional().describe(
    "Optional secondary line describing the item. It is clipped to a single line in the feed cell, so " +
    "anything past the first few words is never read."
  ).meta({ translatable: true }),
  time: z.string().describe("Short relative time label (e.g. '2m', '1h', '3d').").meta({ translatable: true }),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`. When set + category " +
    "present, the icon marker takes the category color and legend can filter items by category."
  ),
});

export const ModuleActivityFeedDataSchema = z.object({
  items: z.array(ModuleActivityFeedItemSchema).describe(
    "Ordered activity entries — rendered in a 3-row × N-col horizontal grid that scrolls."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories for grouping items (release/incident/decision, etc.). When set, " +
    "legend renders category chips + hover-filter dims non-matching items."
  ),
});

export const ModuleActivityFeedSchema = z.object({
  id: z.string(),
  type: z.literal('activity-feed'),
  title: z.string().meta({ translatable: true }),
  data: ModuleActivityFeedDataSchema.optional(),
  variant: z.enum(['icon', 'number']).optional().describe(
    "'icon' renders the item's icon; 'number' renders a mono index (01, 02…). Default 'icon'."
  ),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show a chronological activity feed as a horizontally-scrolling grid — who did what to what, when. " +
  "Best for changelogs, audit logs, team activity streams."
);

export const ModuleMetricListItemSchema = z.object({
  id: z.union([z.string(), z.number()]),
  icon: z.enum([
    'euro',
    'users',
    'user-plus',
    'activity',
    'clock',
    'globe',
    'zap',
    'check-circle',
    'alert-triangle',
  ]).describe("Icon name — resolved to a Lucide icon inside the module."),
  tint: z.string().describe("Icon tint color — CSS variable string or hex."),
  label: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "Short metric label. It sits on one line next to the tile icon and is clipped, not wrapped, so " +
    "one or two words."
  ).meta({ translatable: true }),
  value: z.string().describe("Formatted metric value (free-form, e.g. '€12,480', '3.2%', '128ms')."),
  trend: z.enum(['up', 'down', 'flat']).describe("Direction of the trend arrow."),
  pct: z.number().describe("Percentage change shown next to the trend arrow."),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`. When set + category " +
    "present, metrics are grouped into sub-sections with headers colored by category."
  ),
});

export const ModuleMetricListDataSchema = z.object({
  metrics: z.array(ModuleMetricListItemSchema).describe(
    "Metric tiles rendered in a 3-row × N-col horizontal grid that scrolls."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories for grouping metrics (revenue/product/ops, etc.). When set, " +
    "metrics render in section-groups with a color-accent header per category."
  ),
});

export const ModuleMetricListSchema = z.object({
  id: z.string(),
  type: z.literal('metric-list'),
  title: z.string().meta({ translatable: true }),
  data: ModuleMetricListDataSchema.optional(),
  variant: z.enum(['icon', 'number']).optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show KPI-style metric tiles with label, formatted value, and a trend indicator, laid out in a 3-row " +
  "× N-col horizontal grid."
);

export const ModuleDeltaValueSchema = z.object({
  id: ElementIdField,
  value: z.string().describe("Display string for the value (e.g. '€12,480', '3.2%', '128ms')."),
  label: z.string().max(LABEL_MAX_CHARS.caption).optional().describe(
    "Optional short label (e.g. 'Jan', 'Q1'). It is printed under its value inside the arrow sequence, " +
    "where 2+ values share one cell."
  ).meta({ translatable: true }),
  highlighted: z.boolean().optional().describe(
    "Emphasize this value visually (larger, bolder, fg color). Multiple values can be highlighted."
  ),
});

export const ModuleDeltaMetricSchema = z.object({
  id: z.union([z.string(), z.number()]),
  label: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Metric row label. It heads one cell of the 3-row grid, not the full module width."
  ).meta({ translatable: true }),
  values: z.array(ModuleDeltaValueSchema).describe(
    "2..N values shown as a → separated sequence — earliest to latest."
  ),
  delta: z.number().optional().describe(
    "Optional signed percent change shown top-right. Omit for ambiguous cases (multi-highlight, peaks-and-valleys)."
  ),
  sentiment: z.enum(['good', 'bad', 'neutral']).optional().describe(
    "Color hint for the delta arrow — 'good' emerald, 'bad' rose, 'neutral' subtle."
  ),
});

export const ModuleDeltaComparisonDataSchema = z.object({
  metrics: z.array(ModuleDeltaMetricSchema).describe(
    "KPI rows rendered in a 3-row × N-col horizontal grid — each row is a labeled sequence of before/after values."
  ),
});

export const ModuleDeltaComparisonSchema = z.object({
  id: z.string(),
  type: z.literal('delta-comparison'),
  title: z.string().meta({ translatable: true }),
  data: ModuleDeltaComparisonDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show N metrics as horizontal before-vs-after sequences with optional signed % delta. " +
  "Best for KPI dashboards showing period-over-period change across many metrics."
);

export const ModuleComparisonEntitySchema = z.object({
  id: z.string().describe("Stable id referenced by every metric's `values` map."),
  name: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Entity name shown inline next to each value row (e.g. 'Team Alpha', 'Current setup'). It is " +
    "repeated in every metric cell of the grid, so a long name costs height everywhere."
  ).meta({ translatable: true }),
  tag: z.string().optional().describe("Optional short label (e.g. version 'v2.4'). Not rendered directly, kept for future use.").meta({ translatable: true }),
});

export const ModuleComparisonMetricSchema = z.object({
  id: z.string(),
  label: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Metric name (e.g. 'P50 latency', 'Database'). It heads one cell of the 2-row grid, where several " +
    "cells sit side by side."
  ).meta({ translatable: true }),
  type: z.enum(['numeric', 'text', 'boolean']).optional().describe(
    "Force the value type. Optional — inferred from the first entity's value when omitted."
  ),
  unit: z.string().optional().describe("For numeric metrics — unit shown in the detail line (e.g. 'ms', '%')."),
  higherIsBetter: z.boolean().optional().describe(
    "For numeric metrics — true if the higher value wins, false if the lower value wins. Defaults to false when omitted."
  ),
  preferred: z.string().optional().describe(
    "For text metrics — id of the entity whose value should be highlighted as winner. Omit for no winner."
  ),
  values: z.record(z.string(), z.union([z.number(), z.string().meta({ translatable: true }), z.boolean()])).describe(
    "Map from entity id to value. All values within one metric must share a single type (all numbers, all strings, or all booleans)."
  ),
});

export const ModuleSplitComparisonDataSchema = z.object({
  entities: z.array(ModuleComparisonEntitySchema).min(2).describe(
    "Entities being compared (2 or more). Order determines the row order inside each metric cell."
  ),
  metrics: z.array(ModuleComparisonMetricSchema).describe(
    "Metrics compared across entities — rendered as a 2-row × N-col horizontal grid of cells, each cell showing the metric label plus one value row per entity."
  ),
});

export const ModuleSplitComparisonSchema = z.object({
  id: z.string(),
  type: z.literal('split-comparison'),
  title: z.string().meta({ translatable: true }),
  data: ModuleSplitComparisonDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Head-to-head comparison across N entities and M metrics. Each metric cell shows entity name + bar/text/checkmark. " +
  "Best for team vs team, plan vs plan, before vs after, or any structured multi-attribute comparison."
);

export const ModuleSocialProofAvatarPersonSchema = z.object({
  id: ElementIdField,
  initials: z.string().min(1).max(2).describe(
    "Short avatar text — 1 or 2 uppercase letters (e.g. 'MK', 'A'). Longer strings are rejected by the schema."
  ),
  shade: z.number().optional().describe("HSL lightness 0..100 for the neutral avatar background. Default 20."),
});

export const ModuleSocialProofStatVisualSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('avatars'),
    people: z.array(ModuleSocialProofAvatarPersonSchema).min(2).max(5).describe(
      "2 to 5 avatar-initials rendered as an overlapping cluster."
    ),
    extraCount: z.number().optional().describe("Trailing '+N' overflow chip."),
  }),
  z.object({
    kind: z.literal('bars'),
    data: z.array(z.number()).min(3).describe(
      "Mini bar-chart values — bar-per-value, peak highlighted. Bare-minimum bklit-style bar visual."
    ),
  }),
  z.object({
    kind: z.literal('spark'),
    data: z.array(z.number()).min(3).describe(
      "Sparkline series values — thin line with soft gradient fill and terminal dot. Bare-minimum bklit-style line visual."
    ),
  }),
  z.object({
    kind: z.literal('area'),
    data: z.array(z.number()).min(3).describe(
      "Area-chart series values — filled area with soft gradient, no line-stroke on top. Bare-minimum bklit-style area visual."
    ),
  }),
]);

export const ModuleSocialProofStatSchema = z.object({
  id: z.string(),
  label: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Short stat label (e.g. 'Companies'). It heads a fixed-width stat card, not the module width."
  ).meta({ translatable: true }),
  value: z.string().describe("Big display value (e.g. '500+', '99.98%', '142ms')."),
  caption: z.string().describe("Sub-caption text under the value.").meta({ translatable: true }),
  visual: ModuleSocialProofStatVisualSchema,
});

export const ModuleSocialProofStatsDataSchema = z.object({
  stats: z.array(ModuleSocialProofStatSchema).describe(
    "3-4 stat cards, each with label + big value + caption + a visual. Visual kinds: 'avatars' (stacked initials), " +
    "'bars' (mini bar chart), 'spark' (sparkline) or 'area' (filled sparkline — use for cumulative or volume series)."
  ),
});

export const ModuleSocialProofStatsSchema = z.object({
  id: z.string(),
  type: z.literal('social-proof-stats'),
  title: z.string().meta({ translatable: true }),
  data: ModuleSocialProofStatsDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-4 hero stats with mixed visuals (avatars stack, mini bars, ring gauge, sparkline) — best for social proof, credibility landing sections."
);

export const ModuleBigNumberChartDataSchema = z.object({
  series: z.array(z.number()).min(2).max(120).describe(
    "Values per period, oldest first. E.g. weekly open rate in %. Length defines the x-axis span."
  ),
  seriesB: z.array(z.number()).min(2).max(120).optional().describe(
    "Optional second series, same length and same period grid as `series`. Drawn as a second area. " +
    "At most two areas are allowed — there is no third series."
  ),
  seriesBLabel: z.string().max(LABEL_MAX_CHARS.legend).optional().describe(
    "Legend label for the second series (e.g. 'PDF by email'). Defaults to 'Comparison'."
  ).meta({ translatable: true }),
  baseline: z.number().optional().describe(
    "Optional comparison baseline value. Without `seriesB` it is drawn as the flat second area, so " +
    "every legend entry maps to a drawn area. With `seriesB` set it stays a dashed reference line."
  ),
  metricLabel: z.string().max(LABEL_MAX_CHARS.row).describe(
    "Metric name shown above the big number (e.g. 'Open rate'). It shares that line with a trailing " +
    "'now' or 'in <periodLabel> N', so leave room for the suffix."
  ).meta({ translatable: true }),
  description: z.string().optional().describe(
    "Sentence rendered below the big number that explains what the metric measures."
  ).meta({ translatable: true }),
  unit: z.string().optional().describe(
    "Unit rendered after every value (e.g. '%', 'k', 'ms'). Empty string for unitless numbers."
  ),
  periodLabel: z.string().max(LABEL_MAX_CHARS.axisTickPrefix).optional().describe(
    "Singular period name used in captions and tooltips (e.g. 'Week'). It also prefixes every " +
    "x-axis tick, which then reads '<periodLabel> 1' ... '<periodLabel> N'. Defaults to 'Period'."
  ).meta({ translatable: true }),
  periodLabelPlural: z.string().max(LABEL_MAX_CHARS.axisTickPrefix).optional().describe(
    "Plural period name for headline captions (e.g. 'Weeks'). Defaults to 'Periods'."
  ).meta({ translatable: true }),
  seriesLabel: z.string().max(LABEL_MAX_CHARS.legend).optional().describe(
    "Legend label for the primary series (e.g. 'Handout')."
  ).meta({ translatable: true }),
  baselineLabel: z.string().max(LABEL_MAX_CHARS.legend).optional().describe(
    "Legend label for the baseline. Ignored when no baseline is set. Use `seriesBLabel` instead when " +
    "the comparison is a real series rather than a constant."
  ).meta({ translatable: true }),
  deltaUnit: z.string().optional().describe(
    "Unit for delta numbers in captions (e.g. 'percentage points'). Defaults to empty."
  ).meta({ translatable: true }),
});

export const ModuleBigNumberChartSchema = z.object({
  id: z.string(),
  type: z.literal('big-number-chart'),
  title: z.string().meta({ translatable: true }),
  data: ModuleBigNumberChartDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Hero metric shown as a large animated number with an area/line trend chart underneath. " +
  "Compares the current period to an optional baseline (drawn as a dashed reference line) and " +
  "surfaces the delta in the caption. Hover the chart to inspect any period. " +
  "The x-axis is always fully labeled — every period gets its label ('Week 1' … 'Week N'). " +
  "Choose `periodLabel` short enough that all labels fit horizontally without cutoff."
);

export const ModuleListCardItemSchema = z.object({
  id: ElementIdField,
  icon: IconNameSchema.describe("Lucide icon name — resolved to a Lucide icon inside the module."),
  heading: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Card heading. It only has its own card's width, and 3-4 cards sit side by side."
  ).meta({ translatable: true }),
  text: z.string().describe("Body paragraph.").meta({ translatable: true }),
  categoryId: z.string().optional().describe(
    "Optional category id, matches a `ModuleCategory.id` in `data.categories`. When set + category " +
    "present, cards render in section-groups with a color-accent header per category."
  ),
});

export const ModuleListCardsDataSchema = z.object({
  cards: z.array(ModuleListCardItemSchema).describe(
    "3-4 iconic cards with a dot-grid hero zone above the text."
  ),
  categories: z.array(ModuleCategorySchema).min(1).max(6).optional().describe(
    "Optional free-form categories for section-grouping cards. When set, cards render in section " +
    "blocks with a color-accent header per category."
  ),
});

export const ModuleListCardsSchema = z.object({
  id: z.string(),
  type: z.literal('list-cards'),
  title: z.string().meta({ translatable: true }),
  data: ModuleListCardsDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show 3-4 concept cards with a dot-grid iconic hero zone, dashed divider, and a heading + body block."
);

export const ModulePricingTierSchema = z.object({
  id: z.string().describe("Unique tier id (e.g. 'starter', 'pro', 'business')."),
  name: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Display name of the tier. It heads one of 2-4 cards sitting side by side."
  ).meta({ translatable: true }),
  description: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Short one-line audience description shown under the name, inside the same narrow card."
  ).meta({ translatable: true }),
  price: z.number().describe(
    "Monthly price per seat, billed monthly. When the yearly toggle is on, the number is multiplied by (1 - yearlyDiscount)."
  ),
  included: z.array(z.string().max(LABEL_MAX_CHARS.cell).meta({ translatable: true })).describe(
    "Feature names included in this tier. Must be a subset of the top-level `features` list; matched " +
    "by exact string, so both lists share the same per-entry length limit."
  ),
  recommended: z.boolean().optional().describe(
    "Marks this tier as the recommended one — rendered with a highlighted card and pattern band."
  ),
});

export const ModulePricingTiersDataSchema = z.object({
  tiers: z.array(ModulePricingTierSchema).min(2).max(4).describe(
    "2-4 pricing tiers rendered side-by-side (stacked on narrow screens)."
  ),
  features: z.array(z.string().max(LABEL_MAX_CHARS.cell).meta({ translatable: true })).describe(
    "Full feature catalogue shown as a checklist on every tier. Included features get a check, missing " +
    "ones get a dash. Each entry is printed inside a tier card, so it only has that card's width."
  ),
  yearlyDiscount: z.number().optional().describe(
    "Yearly discount as a fraction (0.2 = 20 %). Set to 0 (or omit) to hide the monthly/yearly toggle. Default 0.2."
  ),
  defaultBilling: z.enum(['monthly', 'yearly']).optional().describe(
    "Which billing cycle is preselected. Default 'yearly'."
  ),
  priceSuffix: z.string().optional().describe(
    "Text after the price, e.g. 'per user / month'. Default 'per user / month'."
  ).meta({ translatable: true }),
  recommendedLabel: z.string().optional().describe(
    "Label shown on the recommended tier's top band. Default 'Recommended'."
  ).meta({ translatable: true }),
});

export const ModulePricingTiersSchema = z.object({
  id: z.string(),
  type: z.literal('pricing-tiers'),
  title: z.string().meta({ translatable: true }),
  data: ModulePricingTiersDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Pricing table comparing 2-4 tiers side-by-side. Each card shows name, audience, animated price, " +
  "billing period caption, and a shared feature checklist (included = check, missing = dash). " +
  "Optional monthly/yearly toggle applies a discount factor to every price."
);

export const ModuleRoiCalculatorDataSchema = z.object({
  team: z.number().optional().describe(
    "Initial team size (people). Slider range 1-200. Default 25."
  ),
  presentations: z.number().optional().describe(
    "Initial presentations per person per month. Slider range 1-20. Default 4."
  ),
  hoursSaved: z.number().optional().describe(
    "Initial hours saved per presentation via the tool. Slider range 0.25-3 (step 0.25). Default 0.5."
  ),
  hourlyRate: z.number().optional().describe(
    "Initial internal hourly rate in €. Slider range 40-150 (step 5). Default 75."
  ),
  seatPrice: z.number().optional().describe(
    "License price per seat per month, used to compute yearly cost. Default 30."
  ),
});

export const ModuleRoiCalculatorSchema = z.object({
  id: z.string(),
  type: z.literal('roi-calculator'),
  title: z.string().meta({ translatable: true }),
  data: ModuleRoiCalculatorDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Interactive ROI calculator: four sliders (team size, presentations per month, hours saved per " +
  "presentation, internal hourly rate) drive a live net-savings figure, ROI %, payback period, " +
  "and a value-vs-license-cost comparison chart. Only the slider defaults come from data — the " +
  "user recomputes everything by dragging the sliders."
);

export const ModuleLogoWallIndustrySchema = z.object({
  id: z.string().describe("Machine id used to link logos to industry (e.g. 'software')."),
  label: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Human-readable industry name shown in the legend, next to its color marker."
  ).meta({ translatable: true }),
  color: z.string().describe(
    "CSS color for the legend marker, a handyout palette slot 'var(--chart-1)' through 'var(--chart-5)'."
  ),
});

export const ModuleLogoWallItemSchema = z.object({
  id: z.string().describe("Unique id for the logo card (used as React key)."),
  name: z.string().max(LABEL_MAX_CHARS.caption).describe(
    "Company name rendered next to the logo image. One or two words - it is a caption on a small logo " +
    "tile in the marquee."
  ),
  logo: z.string().describe(
    "Company logo image. Accepts (a) a full URL, (b) a public /path/*.svg, or (c) the shorthand 'lndev:<slug>' which resolves to /lndev-logos/logos/<slug>.svg — served locally from the ln-dev7/logos-apps git submodule (shallow, ~250 MB, 15 017 SVGs). Slug follows ln-dev7 naming: lowercase, hyphens, add '-wordmark' for the icon+text variant (e.g. 'lndev:stripe', 'lndev:visual-studio-code')."
  ),
  industry: z.string().describe(
    "The `id` of an industry in `industries`. Used for hover-filtering via the legend."
  ),
  since: z.number().optional().describe(
    "Optional year the company became a customer, shown in the hover caption."
  ),
});

export const ModuleLogoWallDataSchema = z.object({
  logos: z.array(ModuleLogoWallItemSchema).min(4).describe(
    "Logo tiles to display in the endlessly-scrolling marquee. 10-30 items works best."
  ),
  industries: z.array(ModuleLogoWallIndustrySchema).min(1).describe(
    "Industries used for the legend and hover-filter (dims non-matching tiles)."
  ),
  total: z.number().optional().describe(
    "Headline count above the marquee (e.g. 500). Combined with `totalSuffix`. Default 500."
  ),
  totalSuffix: z.string().optional().describe(
    "Suffix appended after the animated count (e.g. '+'). Default '+'."
  ),
  totalLabel: z.string().optional().describe(
    "Caption below the headline number (e.g. 'companies use Handyout')."
  ).meta({ translatable: true }),
  delta: z.string().optional().describe(
    "Optional short delta caption (e.g. '+12 % vs. last year'). Rendered with a dotted underline treatment, no colored arrow."
  ).meta({ translatable: true }),
  speed: z.number().optional().describe(
    "Auto-scroll speed in pixels per second. Default 30."
  ),
  idleHint: z.string().optional().describe(
    "Hint text shown under the marquee when no tile is hovered. Default 'Drag or swipe to browse'."
  ).meta({ translatable: true }),
});

export const ModuleLogoWallSchema = z.object({
  id: z.string(),
  type: z.literal('logo-wall'),
  title: z.string().meta({ translatable: true }),
  data: ModuleLogoWallDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Endlessly-scrolling customer logo wall with a headline count, a hover-filter legend by industry, " +
  "and a caption line that shows the currently hovered company. Users can drag or wheel-scroll " +
  "to inspect logos; auto-scroll pauses on interaction."
);

export const ModuleBeforeAfterAnnotationSchema = z.object({
  id: z.string().describe("Unique id (used as React key)."),
  x: z.number().describe(
    "Horizontal position of the pin as a percentage of the media width (0-100)."
  ),
  y: z.number().describe(
    "Vertical position of the pin as a percentage of the media height (0-100)."
  ),
  title: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Short label for the changed element (e.g. 'Readable font size'). It is rendered as the label half " +
    "of a legend entry under the media."
  ).meta({ translatable: true }),
  detail: z.string().max(LABEL_MAX_CHARS.label).describe(
    "Concise change delta shown in the list (e.g. '9 pt → 17 pt', 'No CTA → Book a meeting'). It is " +
    "the value half of the same legend entry, on the same line as the title."
  ).meta({ translatable: true }),
});

export const ModuleBeforeAfterAnnotatedDataSchema = z.object({
  beforeSrc: z.string().describe(
    "Public URL of the 'before' screenshot (PNG/JPG/WEBP). Rendered as an <img> filling the frame."
  ),
  afterSrc: z.string().describe(
    "Public URL of the 'after' screenshot (PNG/JPG/WEBP). Rendered as an <img> filling the frame."
  ),
  annotations: z.array(ModuleBeforeAfterAnnotationSchema).describe(
    "Numbered pins overlaid on the after image. Each pin has a title + detail, listed below the media."
  ),
  beforeAlt: z.string().optional().describe("Accessible alt text for the before image.").meta({ translatable: true }),
  afterAlt: z.string().optional().describe("Accessible alt text for the after image.").meta({ translatable: true }),
  beforeLabel: z.string().optional().describe(
    "Segmented control label for the before state. Default 'Before'."
  ).meta({ translatable: true }),
  afterLabel: z.string().optional().describe(
    "Segmented control label for the after state. Default 'After'."
  ).meta({ translatable: true }),
  aspectRatio: z.string().optional().describe(
    "CSS aspect-ratio for the media frame, e.g. '16 / 10'. Default '16 / 10'."
  ),
  defaultMode: z.enum(['before', 'after']).optional().describe(
    "Which state is shown first. Default 'after'."
  ),
});

export const ModuleBeforeAfterAnnotatedSchema = z.object({
  id: z.string(),
  type: z.literal('before-after-annotated'),
  title: z.string().meta({ translatable: true }),
  data: ModuleBeforeAfterAnnotatedDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Before/after comparison with a segmented toggle: numbered pins on the after image call out each " +
  "specific change, and the list below spells out the delta. Great for redesign walk-throughs."
);

export const ModuleBeforeAfterSliderDataSchema = z.object({
  beforeSrc: z.string().describe(
    "Public URL of the 'before' image (PNG/JPG/WEBP). Rendered as an <img> filling the left side of the split."
  ),
  afterSrc: z.string().describe(
    "Public URL of the 'after' image (PNG/JPG/WEBP). Rendered as an <img> revealed to the right of the drag handle."
  ),
  beforeAlt: z.string().optional().describe("Accessible alt text for the before image.").meta({ translatable: true }),
  afterAlt: z.string().optional().describe("Accessible alt text for the after image.").meta({ translatable: true }),
  beforeLabel: z.string().optional().describe(
    "Label overlaid on the left when the handle is not at the far right. Default 'Before'."
  ).meta({ translatable: true }),
  afterLabel: z.string().optional().describe(
    "Label overlaid on the right when the handle is not at the far left. Default 'After'."
  ).meta({ translatable: true }),
  caption: z.string().optional().describe(
    "Caption shown in the footer next to the preset buttons."
  ).meta({ translatable: true }),
  aspectRatio: z.string().optional().describe(
    "CSS aspect-ratio for the media frame, e.g. '16 / 10'. Default '16 / 10'."
  ),
  defaultPosition: z.number().optional().describe(
    "Initial handle position in percent (0 = only after, 100 = only before). Default 50."
  ),
});

export const KeywordWeightSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]).describe(
  "Semantic weight: 3 = core, 2 = important, 1 = mentioned."
);
export const KeywordGroupSchema = z.object({
  id: z.string().describe("Stable group id referenced by items via `group`."),
  label: z.string().max(LABEL_MAX_CHARS.cell).describe(
    "Human-readable group label shown in column headers or tabs. A column header only has its own " +
    "column's width, and a tab sits in a single scrolling row with the others."
  ).meta({ translatable: true }),
});
export const KeywordChipVariantSchema = z.enum(['ghost', 'outline', 'dashed']).describe(
  "Chip visual style. 'ghost' = subtle filled background (high emphasis), 'outline' = solid thin border " +
  "(mid emphasis), 'dashed' = dashed border (low emphasis). If omitted, the variant is derived from " +
  "`weight` automatically: 3 → ghost, 2 → outline, 1 → dashed."
);

export const KeywordItemSchema = z.object({
  id: z.string().optional().describe("Stable item id. Auto-generated if omitted."),
  text: z.string().max(LABEL_MAX_CHARS.label).describe(
    "The keyword itself (short, no punctuation). It is printed inside a chip, and a chip that wraps " +
    "over several lines stops reading as a chip."
  ).meta({ translatable: true }),
  group: z.string().optional().describe(
    "Group id — matches a `KeywordGroup.id`. Items without a match land in the fallback column."
  ),
  weight: KeywordWeightSchema.optional().describe("Semantic weight. Defaults to 2."),
  variant: KeywordChipVariantSchema.optional().describe(
    "Optional visual style override. If omitted, derived from `weight` (3 → ghost, 2 → outline, 1 → dashed)."
  ),
  color: z.string().optional().describe(
    "Optional CSS color for the chip accent (border + subtle fill). Use a handyout palette slot, " +
    "'var(--chart-1)' through 'var(--chart-5)'. If omitted, chips use neutral border tokens. " +
    "Grayscale mode on the module respects this — chart-N vars auto-map to grays."
  ),
  slides: z.array(z.number()).optional().describe(
    "Slide numbers where this keyword appears (internal anchors only, no external links)."
  ),
  description: z.string().optional().describe(
    "One plain-language sentence explaining the keyword (used in keywords-explained detail panel)."
  ).meta({ translatable: true }),
});

export const ModuleKeywordsGroupedDataSchema = z.object({
  items: z.array(KeywordItemSchema).min(4),
  groups: z.array(KeywordGroupSchema).min(1),
  otherLabel: z.string().optional().describe(
    "Fallback column label for items without a matching group. Default 'Other'."
  ).meta({ translatable: true }),
});

export const ModuleKeywordsGroupedSchema = z.object({
  id: z.string(),
  type: z.literal('keywords-grouped'),
  title: z.string().meta({ translatable: true }),
  data: ModuleKeywordsGroupedDataSchema,
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show keywords organized into horizontally-scrolling columns per group, each keyword rendered as a weighted chip. " +
  "Hover-filter by weight via the bottom legend. Best for glossaries, topic clouds, and thematic overviews."
);

export const ModuleKeywordsExplainedDataSchema = z.object({
  items: z.array(KeywordItemSchema).min(4),
  groups: z.array(KeywordGroupSchema).min(1),
  allLabel: z.string().optional().describe("Label of the 'all' tab. Default 'All'.").meta({ translatable: true }),
});

export const ModuleKeywordsExplainedSchema = z.object({
  id: z.string(),
  type: z.literal('keywords-explained'),
  title: z.string().meta({ translatable: true }),
  data: ModuleKeywordsExplainedDataSchema,
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Show keywords as clickable chips grouped by category tabs; the selected keyword expands into a detail panel " +
  "with description, group, weight, and slide-appearance tags. Best for glossaries where each term deserves explanation."
);

export const ModuleBeforeAfterSliderSchema = z.object({
  id: z.string(),
  type: z.literal('before-after-slider'),
  title: z.string().meta({ translatable: true }),
  data: ModuleBeforeAfterSliderDataSchema.optional(),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Before/after comparison with a drag handle: users slide left/right to reveal each state. " +
  "Includes preset buttons (Before / Split / After) and an optional caption."
);

export const KeyTakeawaysVisualSchema = z.discriminatedUnion('type', [
  VisualRefNumberSchema,
  VisualRefIconSchema,
  VisualRefLogoSchema,
]).describe(
  "Optional per-item glyph rendered in the leading column. Defaults to auto-numbering (item position). " +
  "Use `{type:'icon', name:'target'}` for a lucide slug or `{type:'logo', slug:'lndev:stripe'}` " +
  "for a brand logo."
);

export const ModuleKeyTakeawaysItemSchema = z.object({
  heading: z.string().max(LABEL_MAX_CHARS.row).describe(
    "One-line takeaway headline (no trailing period). It spans the module width next to its glyph, " +
    "and each extra line it wraps to pushes the following takeaways down. Named `heading` rather than " +
    "`title` so it cannot be confused with the module's own `title`, which every module type carries."
  ).meta({ translatable: true }),
  body: z.string().describe("One or two sentences backing the headline with a concrete detail.").meta({ translatable: true }),
  slide: z.number().optional().describe(
    "Slide number this takeaway came from (internal anchor only, no external link)."
  ),
  visual: KeyTakeawaysVisualSchema.optional(),
});

export const ModuleKeyTakeawaysSchema = z.object({
  id: z.string(),
  type: z.literal('key-takeaways'),
  title: z.string().meta({ translatable: true }),
  data: z.record(z.string(), ModuleKeyTakeawaysItemSchema),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Auto-generated key takeaways page — never select manually, rendered statically exactly once per " +
  "handyout directly before the feedback page. Content is the key-takeaways section module's `data`, " +
  "keyed by dot-notation position ('1', '2', '3', ...)."
);

export const ModuleFaqItemSchema = z.object({
  category: z.string().max(40).describe(
    "Category label this question belongs to. Repeat the same string across items to group them; " +
    "identical strings must match exactly or the group splits in two. Categories render as tabs above " +
    "the list and the first tab is preselected — there is no state that shows all categories at once, " +
    "so the reader always sees exactly one category and every category has to carry a page on its own: " +
    "write at least two, better three questions per category, because a tab that opens onto a single " +
    "row reads like a bug. The label has to read as a tab: hard limit 40 characters, one or two words. " +
    "At most 5 items per category are rendered — every further item carrying the same category is " +
    "dropped without a warning, so write at most 5 questions per category."
  ).meta({ translatable: true }),
  question: z.string().max(130).describe(
    "The question, phrased the way the audience would ask it. Hard limit 130 characters: the list is " +
    "an accordion and the question is the only text a collapsed row shows, so a question that wraps " +
    "to three lines makes its row three times as tall as the ~52px it should take."
  ).meta({ translatable: true }),
  answer: z.string().max(500).describe(
    "Two to four sentences answering it directly, no marketing filler. Hard limit 500 characters. " +
    "Only one answer is open at a time and the list scrolls inside the module, so the answer does not " +
    "have to fit a page — but on the narrowest supported viewport (375x667) an answer past this length " +
    "pushes every question under it out of view while it is open."
  ).meta({ translatable: true }),
});

export const ModuleFaqSchema = z.object({
  id: z.string(),
  type: z.literal('faq'),
  title: z.string().meta({ translatable: true }),
  data: z.record(z.string(), ModuleFaqItemSchema),
  grayscale: GrayscaleField,
  ...ModuleStyleFields,
}).describe(
  "Auto-generated FAQ page — never select manually, rendered statically exactly once per handyout " +
  "directly before the feedback page. Content is read from the presentation's `faq` column, keyed by " +
  "dot-notation position ('1', '2', '3', ...). It renders as a single page: category tabs above an " +
  "accordion list that scrolls inside the module, with one answer open at a time. The first tab is " +
  "preselected and no state shows all categories at once, so the reader always sees exactly one " +
  "category — give every category at least two, better three questions, because a category that " +
  "opens onto a single row reads like a bug. At most 5 items per category are rendered — anything " +
  "beyond that is dropped silently and never reaches the reader."
);

export const ModuleTypeSchema = z.discriminatedUnion('type', [
  ModuleChartBarSchema,
  ModuleChartLineSchema,
  ModuleChartPieSchema,
  ModuleChartScatterSchema,
  ModuleChartAreaSchema,
  ModuleImageSchema,
  ModuleProgressBarSchema,
  ModuleQuoteSchema,
  ModuleFeatureComparisonSchema,
  ModuleRingKpisSchema,
  ModuleMetricTabsChartSchema,
  ModuleBreakdownSchema,
  ModuleDualMetricSchema,
  ModuleStageBarsSchema,
  ModuleInsightPanelSchema,
  ModuleDataTableSchema,
  ModuleIntroSchema,
  ModuleTOCSchema,
  ModuleFeedbackSchema,
  ModulePyramidSchema,
  ModuleDottedMapSchema,
  ModuleChartRadialBarSchema,
  ModuleChartCandlestickSchema,
  ModuleChartFunnelSchema,
  ModuleChartHeatmapSchema,
  ModuleChartProfitLossSchema,
  ModuleChartRadarSchema,
  ModuleChartSankeySchema,
  ModuleChartSunburstSchema,
  ModuleProgressStagesSchema,
  ModuleForkJoinSchema,
  ModuleGanttPhasesSchema,
  ModuleParallelStreamsSchema,
  ModuleSegmentedProgressSchema,
  ModuleStateMachineSchema,
  ModuleTimelineStepperSchema,
  ModuleQuadrantMatrixSchema,
  ModuleCorrelationHeatmapSchema,
  ModuleSkillLevelMatrixSchema,
  ModuleNodeCycleSchema,
  ModuleSegmentedCycleSchema,
  ModuleEcosystemMapSchema,
  ModuleHierarchyTreeSchema,
  ModuleParallelCategoryTreesSchema,
  ModuleActivityFeedSchema,
  ModuleMetricListSchema,
  ModuleDeltaComparisonSchema,
  ModuleSplitComparisonSchema,
  ModuleSocialProofStatsSchema,
  ModuleListCardsSchema,
  ModuleBigNumberChartSchema,
  ModulePricingTiersSchema,
  ModuleRoiCalculatorSchema,
  ModuleLogoWallSchema,
  ModuleBeforeAfterAnnotatedSchema,
  ModuleBeforeAfterSliderSchema,
  ModuleKeywordsGroupedSchema,
  ModuleKeywordsExplainedSchema,
  ModuleKeyTakeawaysSchema,
  ModuleFaqSchema,
]);

export const ModulesArraySchema = z.array(ModuleTypeSchema).max(3);

export const PageModuleSchema = z.object({
  modules: ModulesArraySchema,
  author: z.any().optional(),
  title: z.string().meta({ translatable: true }),
  slideId: z.string().optional(),
  totalPages: z.number().optional(),
  summary: z.string().optional().meta({ translatable: true })
});

export type AllModuleTypeNames = z.infer<typeof ModuleTypeSchema>['type'];

export const AUTO_GENERATED_TYPES = [
  'intro',
  'toc',
  'key-takeaways',
  'faq',
  'feedback',
] as const;
export type AutoGeneratedType = (typeof AUTO_GENERATED_TYPES)[number];

export type DraggableModuleType = Exclude<
  AllModuleTypeNames,
  AutoGeneratedType
>;

export const MODULE_CATEGORIES: Record<string, string> = {
  bar: "charts",
  line: "charts",
  pie: "charts",
  scatter: "charts",
  area: "charts",
  progress: "overview",
  "feature-comparison": "overview",
  table: "overview",
  "ring-kpis": "overview",
  "metric-tabs-chart": "charts",
  breakdown: "charts",
  "dual-metric": "overview",
  "stage-bars": "charts",
  "insight-panel": "texts",
  quote: "texts",
  image: "media",
  intro: "overview",
  toc: "overview",
  feedback: "overview",
  pyramid: "overview",
  "progress-stages": "overview",
  "fork-join": "overview",
  "gantt-phases": "overview",
  "parallel-streams": "overview",
  "segmented-progress": "overview",
  "state-machine": "overview",
  "timeline-stepper": "overview",
  "quadrant-matrix": "overview",
  "correlation-heatmap": "charts",
  "skill-level-matrix": "overview",
  "dotted-map": "media",
  "radial-bar": "charts",
  candlestick: "charts",
  funnel: "charts",
  heatmap: "charts",
  "profit-loss": "charts",
  radar: "charts",
  sankey: "charts",
  sunburst: "charts",
  "node-cycle": "overview",
  "segmented-cycle": "overview",
  "ecosystem-map": "overview",
  "hierarchy-tree": "overview",
  "parallel-category-trees": "overview",
  "activity-feed": "texts",
  "metric-list": "overview",
  "delta-comparison": "overview",
  "split-comparison": "overview",
  "social-proof-stats": "overview",
  "list-cards": "texts",
  "big-number-chart": "charts",
  "pricing-tiers": "overview",
  "roi-calculator": "overview",
  "logo-wall": "media",
  "before-after-annotated": "media",
  "before-after-slider": "media",
  "keywords-grouped": "texts",
  "keywords-explained": "texts",
  "key-takeaways": "overview",
  faq: "overview",
};


export type ChartDataPoint = z.infer<typeof ModuleChartDataPointSchema>;
export type ModuleChartSeriesMeta = z.infer<typeof ModuleChartSeriesMetaSchema>;
export type ModuleCategory = z.infer<typeof ModuleCategorySchema>;
export type VisualRefIcon = z.infer<typeof VisualRefIconSchema>;
export type VisualRefImage = z.infer<typeof VisualRefImageSchema>;
export type VisualRefLogo = z.infer<typeof VisualRefLogoSchema>;
export type VisualRefNumber = z.infer<typeof VisualRefNumberSchema>;
export type VisualRef = z.infer<typeof VisualRefSchema>;
export type ModuleReferenceArea = z.infer<typeof ModuleReferenceAreaSchema>;
export type ImageModule = z.infer<typeof ModuleImageDataSchema>;
export type ModuleProgressCompositionSegments = z.infer<typeof ModuleProgressCompositionSegmentsSchema>;
export type ModuleProgressCompositionItem = z.infer<typeof ModuleProgressCompositionItemSchema>;
export type ModuleProgressListStatus = z.infer<typeof ModuleProgressListStatusSchema>;
export type ModuleProgressListItem = z.infer<typeof ModuleProgressListItemSchema>;
export type ModuleProgressBarData = z.infer<typeof ModuleProgressBarDataSchema>;
export type QuoteData = z.infer<typeof ModuleQuoteDataSchema>;
export type ModuleChartBar = z.infer<typeof ModuleChartBarSchema>;
export type ModuleChartLine = z.infer<typeof ModuleChartLineSchema>;
export type ModuleChartPie = z.infer<typeof ModuleChartPieSchema>;
export type ModuleImage = z.infer<typeof ModuleImageSchema>;
export type ModuleProgressBar = z.infer<typeof ModuleProgressBarSchema>;
export type ModuleQuote = z.infer<typeof ModuleQuoteSchema>;
export type ModuleFeatureComparisonRow = z.infer<typeof ModuleFeatureComparisonRowSchema>;
export type ModuleFeatureComparisonPlan = z.infer<typeof ModuleFeatureComparisonPlanSchema>;
export type ModuleFeatureComparisonData = z.infer<typeof ModuleFeatureComparisonDataSchema>;
export type ModuleFeatureComparison = z.infer<typeof ModuleFeatureComparisonSchema>;
export type ValueFormatName = z.infer<typeof ValueFormatSchema>;
export type ModuleRingKpiItem = z.infer<typeof ModuleRingKpiItemSchema>;
export type ModuleRingKpisData = z.infer<typeof ModuleRingKpisDataSchema>;
export type ModuleRingKpis = z.infer<typeof ModuleRingKpisSchema>;
export type ModuleMetricTab = z.infer<typeof ModuleMetricTabSchema>;
export type ModuleMetricTabsChartData = z.infer<typeof ModuleMetricTabsChartDataSchema>;
export type ModuleMetricTabsChart = z.infer<typeof ModuleMetricTabsChartSchema>;
export type ModuleBreakdownItem = z.infer<typeof ModuleBreakdownItemSchema>;
export type ModuleBreakdownData = z.infer<typeof ModuleBreakdownDataSchema>;
export type ModuleBreakdown = z.infer<typeof ModuleBreakdownSchema>;
export type ModuleDualMetricItem = z.infer<typeof ModuleDualMetricItemSchema>;
export type ModuleDualMetricPeriod = z.infer<typeof ModuleDualMetricPeriodSchema>;
export type ModuleDualMetricData = z.infer<typeof ModuleDualMetricDataSchema>;
export type ModuleDualMetric = z.infer<typeof ModuleDualMetricSchema>;
export type ModuleStage = z.infer<typeof ModuleStageSchema>;
export type ModuleStageRange = z.infer<typeof ModuleStageRangeSchema>;
export type ModuleStageBarsData = z.infer<typeof ModuleStageBarsDataSchema>;
export type ModuleStageBars = z.infer<typeof ModuleStageBarsSchema>;
export type ModuleInsightItem = z.infer<typeof ModuleInsightItemSchema>;
export type ModuleInsightPanelData = z.infer<typeof ModuleInsightPanelDataSchema>;
export type ModuleInsightPanel = z.infer<typeof ModuleInsightPanelSchema>;
export type DataTableColumnFormat = z.infer<typeof DataTableColumnFormatSchema>;
export type ModuleDataTableColumn = z.infer<typeof ModuleDataTableColumnSchema>;
export type ModuleDataTableRow = z.infer<typeof ModuleDataTableRowSchema>;
export type ModuleDataTableData = z.infer<typeof ModuleDataTableDataSchema>;
export type ModuleDataTable = z.infer<typeof ModuleDataTableSchema>;
export type ModuleType = z.infer<typeof ModuleTypeSchema>;
export type ModulesArray = z.infer<typeof ModulesArraySchema>;
export type PageModule = z.infer<typeof PageModuleSchema>;
export type ModuleIntro = z.infer<typeof ModuleIntroSchema>;
export type TOCItem = z.infer<typeof ModuleTOCItemSchema>;
export type ModuleTOC = z.infer<typeof ModuleTOCSchema>;
export type ModuleFeedback = z.infer<typeof ModuleFeedbackSchema>;
export type ProfileData = z.infer<typeof ProfileSchema>;

export type ModuleChartScatterPoint = z.infer<typeof ModuleChartScatterPointSchema>;
export type ModuleChartScatterSeries = z.infer<typeof ModuleChartScatterSeriesSchema>;
export type ModuleChartScatterData = z.infer<typeof ModuleChartScatterDataSchema>;
export type ModuleChartScatter = z.infer<typeof ModuleChartScatterSchema>;
export type ModuleChartArea = z.infer<typeof ModuleChartAreaSchema>;
export type ModulePyramidTier = z.infer<typeof ModulePyramidTierSchema>;
export type PyramidVisualRef = z.infer<typeof PyramidVisualRefSchema>;
export type ModulePyramidData = z.infer<typeof ModulePyramidDataSchema>;
export type ModulePyramid = z.infer<typeof ModulePyramidSchema>;
export type ModuleDottedMapPin = z.infer<typeof ModuleDottedMapPinSchema>;
export type ModuleDottedMapData = z.infer<typeof ModuleDottedMapDataSchema>;
export type ModuleDottedMap = z.infer<typeof ModuleDottedMapSchema>;

export type ModuleChartRadialBar = z.infer<typeof ModuleChartRadialBarSchema>;

export type ModuleChartCandlestickPoint = z.infer<typeof ModuleChartCandlestickPointSchema>;
export type ModuleChartCandlestickData = z.infer<typeof ModuleChartCandlestickDataSchema>;
export type ModuleChartCandlestick = z.infer<typeof ModuleChartCandlestickSchema>;
export type ModuleChartFunnelStage = z.infer<typeof ModuleChartFunnelStageSchema>;
export type ModuleChartFunnelData = z.infer<typeof ModuleChartFunnelDataSchema>;
export type ModuleChartFunnel = z.infer<typeof ModuleChartFunnelSchema>;
export type ModuleChartHeatmapPoint = z.infer<typeof ModuleChartHeatmapPointSchema>;
export type ModuleChartHeatmapData = z.infer<typeof ModuleChartHeatmapDataSchema>;
export type ModuleChartHeatmap = z.infer<typeof ModuleChartHeatmapSchema>;
export type ModuleChartProfitLossPoint = z.infer<typeof ModuleChartProfitLossPointSchema>;
export type ModuleChartProfitLossData = z.infer<typeof ModuleChartProfitLossDataSchema>;
export type ModuleChartProfitLoss = z.infer<typeof ModuleChartProfitLossSchema>;
export type ModuleChartRadarMetric = z.infer<typeof ModuleChartRadarMetricSchema>;
export type ModuleChartRadarSeries = z.infer<typeof ModuleChartRadarSeriesSchema>;
export type ModuleChartRadarData = z.infer<typeof ModuleChartRadarDataSchema>;
export type ModuleChartRadar = z.infer<typeof ModuleChartRadarSchema>;
export type ModuleChartSankeyNode = z.infer<typeof ModuleChartSankeyNodeSchema>;
export type ModuleChartSankeyLink = z.infer<typeof ModuleChartSankeyLinkSchema>;
export type ModuleChartSankeyData = z.infer<typeof ModuleChartSankeyDataSchema>;
export type ModuleChartSankey = z.infer<typeof ModuleChartSankeySchema>;
export type ModuleChartSunburstNode = z.infer<typeof ModuleChartSunburstNodeSchema>;
export type ModuleChartSunburstData = z.infer<typeof ModuleChartSunburstDataSchema>;
export type ModuleChartSunburst = z.infer<typeof ModuleChartSunburstSchema>;

export type ModuleProgressStage = z.infer<typeof ModuleProgressStageSchema>;
export type ModuleProgressData = z.infer<typeof ModuleProgressDataSchema>;
export type ModuleProgressStages = z.infer<typeof ModuleProgressStagesSchema>;
export type ModuleForkJoinNode = z.infer<typeof ModuleForkJoinNodeSchema>;
export type ModuleForkJoinEdge = z.infer<typeof ModuleForkJoinEdgeSchema>;
export type ModuleForkJoinData = z.infer<typeof ModuleForkJoinDataSchema>;
export type ModuleForkJoin = z.infer<typeof ModuleForkJoinSchema>;
export type ModuleGanttPhase = z.infer<typeof ModuleGanttPhaseSchema>;
export type ModuleGanttPhasesData = z.infer<typeof ModuleGanttPhasesDataSchema>;
export type ModuleGanttPhases = z.infer<typeof ModuleGanttPhasesSchema>;
export type ModuleParallelStreamStep = z.infer<typeof ModuleParallelStreamStepSchema>;
export type ModuleParallelStreamTrack = z.infer<typeof ModuleParallelStreamTrackSchema>;
export type ModuleParallelStreamMilestone = z.infer<typeof ModuleParallelStreamMilestoneSchema>;
export type ModuleParallelStreamsData = z.infer<typeof ModuleParallelStreamsDataSchema>;
export type ModuleParallelStreams = z.infer<typeof ModuleParallelStreamsSchema>;
export type ModuleSegmentedProgressSegment = z.infer<typeof ModuleSegmentedProgressSegmentSchema>;
export type ModuleSegmentedProgressData = z.infer<typeof ModuleSegmentedProgressDataSchema>;
export type ModuleSegmentedProgress = z.infer<typeof ModuleSegmentedProgressSchema>;
export type ModuleStateMachineNode = z.infer<typeof ModuleStateMachineNodeSchema>;
export type ModuleStateMachineTransitionType = z.infer<typeof ModuleStateMachineTransitionTypeSchema>;
export type ModuleStateMachineTransition = z.infer<typeof ModuleStateMachineTransitionSchema>;
export type ModuleStateMachineData = z.infer<typeof ModuleStateMachineDataSchema>;
export type ModuleStateMachine = z.infer<typeof ModuleStateMachineSchema>;
export type ModuleTimelineStepperStep = z.infer<typeof ModuleTimelineStepperStepSchema>;
export type ModuleTimelineStepperData = z.infer<typeof ModuleTimelineStepperDataSchema>;
export type ModuleTimelineStepper = z.infer<typeof ModuleTimelineStepperSchema>;
export type ModuleQuadrantMatrixItem = z.infer<typeof ModuleQuadrantMatrixItemSchema>;
export type ModuleQuadrantMatrixData = z.infer<typeof ModuleQuadrantMatrixDataSchema>;
export type ModuleQuadrantMatrix = z.infer<typeof ModuleQuadrantMatrixSchema>;
export type ModuleCorrelationHeatmapData = z.infer<typeof ModuleCorrelationHeatmapDataSchema>;
export type ModuleCorrelationHeatmap = z.infer<typeof ModuleCorrelationHeatmapSchema>;
export type ModuleSkillLevelMatrixData = z.infer<typeof ModuleSkillLevelMatrixDataSchema>;
export type ModuleSkillLevelMatrix = z.infer<typeof ModuleSkillLevelMatrixSchema>;
export type ModuleCyclePhase = z.infer<typeof ModuleCyclePhaseSchema>;
export type ModuleCycleData = z.infer<typeof ModuleCycleDataSchema>;
export type ModuleNodeCycle = z.infer<typeof ModuleNodeCycleSchema>;
export type ModuleSegmentedCycle = z.infer<typeof ModuleSegmentedCycleSchema>;
export type ModuleEcosystemSegmentStatus = z.infer<typeof ModuleEcosystemSegmentStatusSchema>;
export type ModuleEcosystemSegment = z.infer<typeof ModuleEcosystemSegmentSchema>;
export type ModuleEcosystemRing = z.infer<typeof ModuleEcosystemRingSchema>;
export type ModuleEcosystemCore = z.infer<typeof ModuleEcosystemCoreSchema>;
export type ModuleEcosystemMarketplace = z.infer<typeof ModuleEcosystemMarketplaceSchema>;
export type ModuleEcosystemMapData = z.infer<typeof ModuleEcosystemMapDataSchema>;
export type ModuleEcosystemMap = z.infer<typeof ModuleEcosystemMapSchema>;
export type ModuleFlatHierarchyNode = z.infer<typeof ModuleFlatHierarchyNodeSchema>;
export type ModuleHierarchyTreeColumn = z.infer<typeof ModuleHierarchyTreeColumnSchema>;
export type ModuleHierarchyTreeData = z.infer<typeof ModuleHierarchyTreeDataSchema>;
export type ModuleHierarchyTree = z.infer<typeof ModuleHierarchyTreeSchema>;
export type ModuleParallelCategoryTrees = z.infer<typeof ModuleParallelCategoryTreesSchema>;
export type ModuleActivityFeedItem = z.infer<typeof ModuleActivityFeedItemSchema>;
export type ModuleActivityFeedData = z.infer<typeof ModuleActivityFeedDataSchema>;
export type ModuleActivityFeed = z.infer<typeof ModuleActivityFeedSchema>;
export type ModuleMetricListItem = z.infer<typeof ModuleMetricListItemSchema>;
export type ModuleMetricListData = z.infer<typeof ModuleMetricListDataSchema>;
export type ModuleMetricList = z.infer<typeof ModuleMetricListSchema>;
export type ModuleDeltaValue = z.infer<typeof ModuleDeltaValueSchema>;
export type ModuleDeltaMetric = z.infer<typeof ModuleDeltaMetricSchema>;
export type ModuleDeltaComparisonData = z.infer<typeof ModuleDeltaComparisonDataSchema>;
export type ModuleDeltaComparison = z.infer<typeof ModuleDeltaComparisonSchema>;
export type ModuleComparisonEntity = z.infer<typeof ModuleComparisonEntitySchema>;
export type ModuleComparisonMetric = z.infer<typeof ModuleComparisonMetricSchema>;
export type ModuleSplitComparisonData = z.infer<typeof ModuleSplitComparisonDataSchema>;
export type ModuleSplitComparison = z.infer<typeof ModuleSplitComparisonSchema>;
export type ModuleSocialProofAvatarPerson = z.infer<typeof ModuleSocialProofAvatarPersonSchema>;
export type ModuleSocialProofStatVisual = z.infer<typeof ModuleSocialProofStatVisualSchema>;
export type ModuleSocialProofStat = z.infer<typeof ModuleSocialProofStatSchema>;
export type ModuleSocialProofStatsData = z.infer<typeof ModuleSocialProofStatsDataSchema>;
export type ModuleSocialProofStats = z.infer<typeof ModuleSocialProofStatsSchema>;
export type ModuleListCardItem = z.infer<typeof ModuleListCardItemSchema>;
export type ModuleListCardsData = z.infer<typeof ModuleListCardsDataSchema>;
export type ModuleListCards = z.infer<typeof ModuleListCardsSchema>;
export type ModuleBigNumberChartData = z.infer<typeof ModuleBigNumberChartDataSchema>;
export type ModuleBigNumberChart = z.infer<typeof ModuleBigNumberChartSchema>;
export type ModulePricingTier = z.infer<typeof ModulePricingTierSchema>;
export type ModulePricingTiersData = z.infer<typeof ModulePricingTiersDataSchema>;
export type ModulePricingTiers = z.infer<typeof ModulePricingTiersSchema>;
export type ModuleRoiCalculatorData = z.infer<typeof ModuleRoiCalculatorDataSchema>;
export type ModuleRoiCalculator = z.infer<typeof ModuleRoiCalculatorSchema>;
export type ModuleLogoWallIndustry = z.infer<typeof ModuleLogoWallIndustrySchema>;
export type ModuleLogoWallItem = z.infer<typeof ModuleLogoWallItemSchema>;
export type ModuleLogoWallData = z.infer<typeof ModuleLogoWallDataSchema>;
export type ModuleLogoWall = z.infer<typeof ModuleLogoWallSchema>;
export type ModuleBeforeAfterAnnotation = z.infer<typeof ModuleBeforeAfterAnnotationSchema>;
export type ModuleBeforeAfterAnnotatedData = z.infer<typeof ModuleBeforeAfterAnnotatedDataSchema>;
export type ModuleBeforeAfterAnnotated = z.infer<typeof ModuleBeforeAfterAnnotatedSchema>;
export type ModuleBeforeAfterSliderData = z.infer<typeof ModuleBeforeAfterSliderDataSchema>;
export type ModuleBeforeAfterSlider = z.infer<typeof ModuleBeforeAfterSliderSchema>;
export type KeywordWeight = z.infer<typeof KeywordWeightSchema>;
export type KeywordGroup = z.infer<typeof KeywordGroupSchema>;
export type KeywordItem = z.infer<typeof KeywordItemSchema>;
export type KeywordChipVariant = z.infer<typeof KeywordChipVariantSchema>;
export type ModuleKeywordsGroupedData = z.infer<typeof ModuleKeywordsGroupedDataSchema>;
export type ModuleKeywordsGrouped = z.infer<typeof ModuleKeywordsGroupedSchema>;
export type ModuleKeywordsExplainedData = z.infer<typeof ModuleKeywordsExplainedDataSchema>;
export type ModuleKeywordsExplained = z.infer<typeof ModuleKeywordsExplainedSchema>;
export type KeyTakeawaysVisual = z.infer<typeof KeyTakeawaysVisualSchema>;
export type ModuleKeyTakeawaysItem = z.infer<typeof ModuleKeyTakeawaysItemSchema>;
export type ModuleKeyTakeawaysData = z.infer<typeof ModuleKeyTakeawaysSchema>['data'];
export type ModuleKeyTakeaways = z.infer<typeof ModuleKeyTakeawaysSchema>;
export type ModuleFaqItem = z.infer<typeof ModuleFaqItemSchema>;
export type ModuleFaqData = z.infer<typeof ModuleFaqSchema>['data'];
export type ModuleFaq = z.infer<typeof ModuleFaqSchema>;